// src/hooks/useProvisions.js
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { classifyFetchFailure } from "../lib/importErrors";
import { isMalformedGalleyDraft, measureSteps } from "../lib/galleyDraft";
import { createSupabaseClient } from "../lib/supabaseClient";
import { classifyFetchError, classifyAuthFailure } from "../lib/classifyFetchError";
import { reportAuthRejected, isPollingOpen } from "../lib/authHealth";
import { scheduleHoldRetry } from "../lib/holdRetry";
import { inviteFailureNotice } from "../lib/inviteFailure";
import { useConnectivity } from "../contexts/ConnectivityContext";
import { normalizeHouseholdPhoto, renderMealPhoto, mealPhotoPaths } from "../lib/image";
import { trace } from "@opentelemetry/api";
import { fetchLiveMealLinks, linksToRelease, releaseMealLinks, takeMealOffList, droppedIngredientIds } from "../lib/mealLinks";
import { cooksFromRows, resolveCookId } from "../lib/cooks";
import { setKindFailure } from "../lib/mealPhotos";
import { giveFailure, giftObjectPath, giftExt, GIFT_RENDITIONS } from "../lib/giveMeal";
import { arrivalFailure, giftSourcePaths, acceptCopyPaths } from "../lib/giftArrival";

// RUM custom events ride the provider SplunkOtelWeb.init() registers in src/rum.js
// (same module-level pattern as ActiveHouseholdContext). @splunk/otel-web 3.0.0
// exposes no addRUMEvent — its public API is init / setGlobalAttributes /
// getSessionId / provider — so a custom event is a zero-length span on the OTel
// tracer. With no RUM token the API's no-op provider stands in and this costs nothing.
const tracer = trace.getTracer("ourprovisions-app");

// Replanning starts over (src/lib/mealLinks.js): a meal leaving This Week lets go of
// its list_item_meals links. Best-effort: a failed release is a RUM warning, never an
// error toast, and the fallback is the behaviour before 2026-10-19 (the link stays).
function warnLinkRelease(e, { householdId, mealId, path, links }) {
  console.warn(`[rum] meal_links_release_failed (${path}):`, e?.message || e);
  try {
    // ids only — the meal and household are uuids; never a name, never the message text.
    tracer.startSpan("meal_links_release_failed", {
      attributes: { household_id: householdId, meal_id: mealId, path, links, error_code: e?.code || "unknown" },
    }).end();
  } catch (_e) { /* telemetry never throws into the cook or the remove */ }
}
export async function releaseLinksOrWarn(db, { householdId, mealId, listItemIds, path }) {
  try {
    return await releaseMealLinks(db, { mealId, listItemIds });
  } catch (e) {
    warnLinkRelease(e, { householdId, mealId, path, links: (listItemIds || []).length });
    return null;
  }
}

// A catalog item the meal builder has staged but NOT yet written. The id is a
// client-only placeholder, deterministic on the normalised name so that staging
// the same unmatched name twice resolves to the same row (and bumps it, exactly
// as re-tapping an existing item does) instead of forking a second placeholder.
// materializePendingIngredients swaps these for real ids at Save; nothing with
// a pending id may ever reach meal_ingredients.
const normCatalogName = (str) => (str || "").trim().toLowerCase().replace(/\s+/g, " ");
const PENDING_CATALOG_PREFIX = "pending:";
// Meal occasions (061, Meal Library v1; "drink" added by 069, SPEC_meal_occasion_drink):
// the eight values meals.occasion may hold, in the CHECK constraint's vocabulary.
// The column is an ORDERED list — occasion[0] is the word on the library card; the
// rail matches any element. normalizeOccasion is the one client-side gate before a
// write: unknown values dropped, duplicates removed (the CHECK cannot test
// uniqueness), order kept.
export const MEAL_OCCASIONS = ["breakfast", "lunch", "dinner", "snack", "side", "appetizer", "dessert", "drink"];
export const normalizeOccasion = (list) => {
  const out = [];
  (Array.isArray(list) ? list : []).forEach((o) => {
    const v = String(o || "").trim().toLowerCase();
    if (MEAL_OCCASIONS.includes(v) && !out.includes(v)) out.push(v);
  });
  return out;
};

export const isPendingCatalogId = (id) =>
  typeof id === "string" && id.startsWith(PENDING_CATALOG_PREFIX);

export function useProvisions({ getToken, userId, clerkId, email, fullName, activeHouseholdId, myHouseholds, sessionId, sessionLive }) {
  // INVARIANT (2026-09-12): client state for list rows is keyed by list_item.id.
  // The name is a label, never an identity — two live rows can share one
  // ("Milk" custom beside "Milk" catalog; a reused cycle row). `quantities` and
  // `checked` are maps of list_item.id → value. The one exception is a row that
  // does not exist yet: while an add is in flight the optimistic quantity sits
  // under `pre:<catalog_item_id>` and the reader falls back to it until the
  // poll delivers the real row.
  const [quantities, setQuantities] = useState({});
  const [checked, setChecked] = useState({});
  const listRowsRef = useRef([]);
  const preKey = (catalogItemId) => `pre:${catalogItemId}`;
  const liveRowIdFor = (catalogItemId) =>
    listRowsRef.current.find(r => r.catalogItemId === catalogItemId)?.id || null;
  const [prices, setPrices] = useState({});
  const [addedByMap, setAddedByMap] = useState({});
  const [contributorsMap, setContributorsMap] = useState({});
  const [categoryAvgPrices, setCategoryAvgPrices] = useState({});
  const [household, setHousehold] = useState(null);
  const [householdMembers, setHouseholdMembers] = useState([]);
  // Stable per-user referral code (migration 042, spec D9). Returned by bootstrap so the
  // Share-the-app verb needs no lookup and no RPC at send time (D10). Null until bootstrap
  // resolves, and null forever against an un-migrated database — the verb hides itself
  // rather than composing a naked URL, which D8 forbids outright.
  const [referralCode, setReferralCode] = useState(null);
  const [catalogMap, setCatalogMap] = useState({});
  const [listRows, setListRows] = useState([]); // raw surviving RPC rows — source of truth for the SHOP list
  const [hiddenCatalogItems, setHiddenCatalogItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // householdReady — true once THIS household's list has been read successfully
  // at least once. `loading` cannot carry that meaning: it clears on the anon
  // catalog pass, and it clears at the end of loadForHousehold even when the
  // first list tick returned a transient error or a suspect-empty response and
  // set no rows (seen on prod 2026-09-12: a slow cold load landed on Browse
  // with 18 items on the list). The splash gate and the landing decision wait
  // on this, not on loading. Reset on every household load.
  const [householdReady, setHouseholdReady] = useState(false);
  // A5 (Amendment 2026-09-27): "not loaded" must never draw as "empty". These
  // hold the id of the household whose members / catalog have been read at
  // least once; the derived membersLoaded / catalogLoaded compare against the
  // ACTIVE household, so a switch reads as not-loaded until the new place's
  // rows are in. Reset with the rest of the household world on sign-out.
  const [membersLoadedFor, setMembersLoadedFor] = useState(null);
  // The household whose meal_cooks reduce (`cooks`) has been read at least once.
  // A meal with no entry in `cooks` is "never cooked" ONLY once this says so —
  // before that it is "unknown", and the recipe view shows no tags (2026-10-18).
  const [cooksLoadedFor, setCooksLoadedFor] = useState(null);
  const [catalogLoadedFor, setCatalogLoadedFor] = useState(null);
  const [error, setError] = useState(null);
  // Which subsystem owns the message currently in `error`. See failWith/clearErrorFrom.
  const errorSourceRef = useRef(null);
  const { reportTransientFailure, reportSuccess } = useConnectivity();
  // §Polling discipline — every POLLED read runs its failure through this first.
  // An auth failure never retries and never toasts: 'missing' (the wrapper
  // refused on a null token) skips the tick — authHealth is already counting
  // toward session_lost; 'rejected' (401/403 with a token attached) ends the
  // session outright via reportAuthRejected, and the sign-out reset stops every
  // interval. Returns true when the caller must return silently. Reads only —
  // an RLS-denied WRITE is a 403 too, and that is a bug to toast, not a loss.
  const stopOnAuthFailure = (error, status) => {
    const cls = classifyAuthFailure(error, status);
    if (cls === 'rejected') { reportAuthRejected(status); return true; }
    if (cls === 'missing') return true;
    return false;
  };
  const supabaseRef = useRef(null);
  const householdRef = useRef(null);   // mirrors household for use inside callbacks
  const catalogRef = useRef({});       // mirrors catalogMap for use inside callbacks
  const hiddenIdsRef = useRef(new Set());
  const deletedIdsRef = useRef(new Set());
  const refreshCatalogRef = useRef(() => {});
  // Part 3 (passive-viewer provenance sync). The 2s poll is the only thing
  // that knows the shared list moved, but mealProvenance lives in App.js —
  // this ref is that hook→App channel, the same shape refreshCatalogRef
  // already uses for the catalog poll. App assigns it; the poll calls it.
  const onListChangedRef = useRef(() => {});
  // Fingerprint of the list the poll last saw. "" until the first load, so
  // the initial fetch never counts as a change — arriving on a surface
  // already triggers its own provenance refresh, and double-firing would
  // just be a duplicate query.
  const listFingerprintRef = useRef("");
  const hiddenCatalogItemsRef = useRef([]);
  const internalUserIdRef = useRef(null);
  const householdMembersRef = useRef([]);
  const clerkIdRef = useRef(null);
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const wrappingUpRef = useRef(false);
  // Items with an in-flight local quantity write. The 2s poll must not
  // overwrite these from the DB until the write confirms, or it'll briefly
  // snap the optimistic value back to the stale server value (5→4→5 flicker).
  const pendingQtyRef = useRef(new Set());
  // Same guard for optimistic check state: while a toggle write is in flight,
  // the 2s poll must not overwrite the optimistic value with the stale server
  // status (Bug 3: toggle bounces / takes ~3 taps to stick). Keyed by name,
  // matching `checked`.
  const pendingCheckRef = useRef(new Set());
  // In-flight guard for the 2s list poll. On a slow (3G) connection a single
  // loadListItems cycle (get_list_items RPC → contributors query → setStates) can
  // outlast the 2s interval; without this the setInterval keeps firing and the
  // cycles overlap and pile up (the "never settles / count climbs" symptom). If a
  // cycle is still running, the next tick is skipped and the poll effectively
  // backs off to the cycle duration.
  const loadingListRef = useRef(false);
  const [activeCycle, setActiveCycle] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const activeCycleRef = useRef(null);
  const activeSessionRef = useRef(null);
  // In-store capture (SPEC_shop_lens_instore_capture.md, migration 046).
  // partnerSession — another member's currently-open session, which feeds the
  //   store prompt's FIRST chip (D10: ask, don't guess; sessions are per person).
  // storeSuggestions — the household's distinct store_name_raw values, most
  //   recent first, for the remaining chips.
  // checkedByMap — listItemId → internal user id of the last `checked` event,
  //   read from list_item_events (D8: history lives in events; the never-written
  //   list_items.checked_by column is dead). Drives the tray's initials.
  // eventSeqRef — per-session client counter written as `sequence`. Tiebreaker
  //   only; created_at (server now()) is the ordering truth.
  // sessionStartingRef — in-flight startSession promise, so two quick taps on
  //   the first check of a trip cannot open two sessions during the GPS wait.
  const [partnerSession, setPartnerSession] = useState(null);
  const [storeSuggestions, setStoreSuggestions] = useState([]);
  const [checkedByMap, setCheckedByMap] = useState({});
  // Plan board placements (047, amended by 050 — SPEC_meal_planning_v1_board_queue.md):
  // meal_id → { sortOrder, readyAt, cookedAt, skippedAt } for the active
  // household. THE BOARD IS A QUEUE OF OPEN PLACEMENTS: a meal is on it iff
  // cookedAt and skippedAt are both null, in sortOrder, position 0 up next.
  // Membership comes from here, never from the list; the list only supplies
  // each card's STATE (what is still to buy). Loaded and polled by App.js ONLY
  // while Plan is visible, the same scoping as the meals poll, so an idle
  // client queries nothing new. placementsRef mirrors the state for use inside
  // callbacks; reorderPendingRef holds the poll off while a reorder write is
  // in flight so a stale read cannot snap the optimistic order back (the
  // 5→4→5 flicker family, one surface over).
  const [placements, setPlacements] = useState({});
  const placementsRef = useRef({});
  // Cook log (058, SPEC_replan_cook_history.md): meal_id → { lastCookedAt, count }
  // for the active household, reduced client-side from meal_cooks (SELECT only —
  // rows arrive through the cook_meal RPC, never from here). Loaded with the
  // household (Effect 2) and after every markCooked; loadPlacements re-reads it
  // when any cookedAt changed, because a partner's cook changes a placement and
  // the 2s Plan poll is therefore already the change signal — no new poll.
  // Read by madeBefore (D5) and App.js's Leftovers sheet (D4).
  const [cooks, setCooks] = useState({});
  // The cook THIS device just logged, by meal (Photos slice 2, S3): meal_id → the new
  // meal_cooks.id, or null when cook_meal closed nothing (a partner's tap) or the lookup did not
  // find exactly one row. Read by the board's afterglow (rating + a cook-attached photo), which
  // shows no controls without an id. Device-local, like the afterglow; reset on household switch.
  const [cookIdsByMeal, setCookIdsByMeal] = useState({});
  const reorderPendingRef = useRef(0);
  const eventSeqRef = useRef(0);
  const sessionStartingRef = useRef(null);
  const boughtFingerprintRef = useRef("");
  const checkedByRetryRef = useRef(0);
  const myHouseholdsRef = useRef(myHouseholds || []);
  myHouseholdsRef.current = myHouseholds || [];
  const bootstrapHouseholdIdRef = useRef(null);
  const bootstrappedRef = useRef(false);
  const [bootstrapped, setBootstrapped] = useState(false);
  // Amendment 2026-09-27 (A2/A3): a transient first request is a HOLD, never
  // terminal. Each counter bumps to re-run its effect after the backoff (or at
  // once on `online` / visible); each ref counts consecutive transient failures
  // for the current identity (Effect 1) or household (Effect 2) so the delay
  // grows 1→2→4→8→16→30s and resets on success or on a change of subject.
  const [bootstrapAttempt, setBootstrapAttempt] = useState(0);
  const bootstrapHoldRef = useRef({ key: null, failures: 0 });
  const [householdAttempt, setHouseholdAttempt] = useState(0);
  const householdHoldRef = useRef({ key: null, failures: 0 });
  const justJoinedViaInviteRef = useRef(false);
  const realtimeChannelRef = useRef(null);

  // Staple state is per-household row-presence in household_staples (migration
  // 016) — the single source of truth for BOTH global and custom items. Returns
  // the set of catalog_item_ids this household has stapled, so catalog reads can
  // stamp is_staple per item without trusting the dormant catalog_items column.
  async function fetchStapleSet(db, householdId) {
    const { data } = await db
      .from("household_staples")
      .select("catalog_item_id")
      .eq("household_id", householdId);
    return new Set((data || []).map((r) => r.catalog_item_id));
  }

  // Tray attribution — who last checked each bought row, read back from the
  // event log, never from list_items.checked_by (D8). Best-effort by design:
  // against a database where 046 is not applied the select errors, is logged,
  // and the tray simply shows no initials. Latest event per row wins; only a
  // `checked` counts (an `unchecked` that follows it clears the attribution).
  // A partner's event lands a beat AFTER their status update (their client
  // writes the event once the primary write commits), so the poll can see the
  // row bought before its event exists — bounded retries cover that gap
  // without turning this into a query on every 2s tick.
  async function refreshCheckedBy(db, householdId, boughtIds) {
    if (boughtIds.length === 0) { setCheckedByMap({}); return; }
    const { data, error: evErr } = await db
      .from("list_item_events")
      .select("list_item_id, user_id, event_type, created_at, sequence")
      .eq("household_id", householdId)
      .in("list_item_id", boughtIds)
      .in("event_type", ["checked", "unchecked"])
      .order("created_at", { ascending: false })
      .order("sequence", { ascending: false })
      .limit(500);
    if (evErr) { console.warn("refreshCheckedBy:", evErr.message); return; }
    const map = {};
    const seen = new Set();
    (data || []).forEach(e => {
      if (seen.has(e.list_item_id)) return;
      seen.add(e.list_item_id);
      if (e.event_type === "checked") map[e.list_item_id] = e.user_id;
    });
    setCheckedByMap(map);
    // Retry a few ticks while a bought row has no event yet; then let it rest
    // until the bought set changes again.
    const missing = boughtIds.some(id => !map[id]);
    checkedByRetryRef.current = missing ? Math.max(0, checkedByRetryRef.current - 1) : 0;
  }

  async function loadListItems(db, householdId) {
    if (wrappingUpRef.current) return;
    if (loadingListRef.current) return;   // skip this tick — a cycle is still in flight
    loadingListRef.current = true;
    try {
      await loadListItemsInner(db, householdId);
    } finally {
      loadingListRef.current = false;
    }
  }

  async function loadListItemsInner(db, householdId) {
    const { data: items, error: listErr, status: listStatus } = await db
      .rpc("get_list_items_for_household", { p_household_id: householdId });

    if (listErr) {
      if (stopOnAuthFailure(listErr, listStatus)) return;
      if (classifyFetchError(listErr) === 'transient') { reportTransientFailure(); } else { failWith("list", `Could not load list: ${listErr.message}`); }
      return;
    }

    // Suspect-empty guard: a dropped connection can resolve this RPC with no
    // error but zero rows. If we currently hold state, treat it as a transient
    // empty response and bail before any setters run — don't clobber quantities,
    // checks, rows, or contributors to empty. A real clear-list calls the
    // setters directly, not through this poll.
    if (!items || items.length === 0) {
      let hadItems = false;
      setQuantities((prev) => { hadItems = Object.keys(prev).length > 0; return prev; });
      if (hadItems) { reportTransientFailure(); return; }
    }

    // The read succeeded (an empty list here is a real empty list, not a dropped
    // connection — the guard above already returned in that case), so retire any
    // stale "Could not load list" left behind by an earlier failed tick.
    clearErrorFrom("list");

    // Names/categories/staple flags now arrive inline from the list RPC join.
    const catalogNameMap = {};
    items.forEach(it => { catalogNameMap[it.catalog_item_id] = it.name; });

    // Merge full catalog entries (with category + is_staple) so items added by
    // other users render in their correct category section.
    items.forEach(it => {
      if (hiddenIdsRef.current.has(it.catalog_item_id) || deletedIdsRef.current.has(it.catalog_item_id)) return; // never re-add a hidden or just-deleted item
      catalogRef.current[it.name] = {
        ...catalogRef.current[it.name],
        id: it.catalog_item_id, name: it.name,
        category: it.category, is_staple: it.is_staple,
      };
    });
    setCatalogMap(prev => {
      let changed = false;
      const next = { ...prev };
      items.forEach(it => {
        const existing = next[it.name];
        if (hiddenIdsRef.current.has(it.catalog_item_id) || deletedIdsRef.current.has(it.catalog_item_id)) return; // never re-add a hidden or just-deleted item
        if (!existing || existing.category !== it.category || existing.is_staple !== it.is_staple) {
          next[it.name] = {
            ...existing,
            id: it.catalog_item_id, name: it.name,
            category: it.category, is_staple: it.is_staple,
          };
          changed = true;
        }
      });
      return changed ? next : prev;
    });

    // Cheap change signal for the provenance refresh: live row ids paired
    // with their quantities. Every way a meal link can appear or disappear —
    // an add, a removal, a quantity move — changes this string, while a quiet
    // tick leaves it byte-identical. That's what keeps an IDLE client from
    // re-querying provenance every 2 seconds, which is the whole reason the
    // original code carried a "no poll" note.
    const fingerprint = items.map(i => `${i.id}:${i.quantity}`).join(",");
    const listChanged = listFingerprintRef.current !== "" && listFingerprintRef.current !== fingerprint;
    listFingerprintRef.current = fingerprint;

    const listItemIds = items.map(i => i.id);

    let contributorRows = [];
    if (listItemIds.length > 0) {
      const { data: contribs } = await db
        .from("list_item_contributors")
        .select("list_item_id, user_id, quantity_added, added_at")
        .in("list_item_id", listItemIds);
      contributorRows = contribs || [];
    }

    const newQty = {};
    const newChecked = {};
    const newPrices = {};
    const newAddedBy = {};
    const newContributors = {};
    const newListRows = [];

    items.forEach((item) => {
      const name = catalogNameMap[item.catalog_item_id];
      if (!name) return;
      newListRows.push({
        id: item.id,
        catalogItemId: item.catalog_item_id,
        name,
        category: item.category,
        isStaple: item.is_staple,
        quantity: item.quantity,
        status: item.status,
        pricePerUnit: item.price_per_unit != null ? parseFloat(item.price_per_unit) : null,
        addedBy: item.added_by ?? null,
      });
      newQty[item.id] = item.quantity;
      newChecked[item.id] = item.status === "bought";
      if (item.price_per_unit != null) newPrices[name] = parseFloat(item.price_per_unit);
      if (item.added_by != null) newAddedBy[name] = item.added_by;

      const itemContribs = contributorRows
        .filter(c => c.list_item_id === item.id)
        .sort((a, b) => new Date(a.added_at) - new Date(b.added_at))
        .map(c => {
          const profile = householdMembersRef.current?.find(m => m.user_id === c.user_id);
          return {
            userId: c.user_id,
            fullName: profile?.users?.full_name || null,
            clerkId: profile?.users?.clerk_id || null,
            quantityAdded: c.quantity_added,
          };
        });

      if (itemContribs.length > 0) newContributors[name] = itemContribs;
    });

    const mergedPrices = {};
    Object.values(catalogRef.current).forEach(item => {
      if (item.price_hint != null) mergedPrices[item.name] = parseFloat(item.price_hint);
    });
    Object.assign(mergedPrices, newPrices);
    listRowsRef.current = newListRows;
    setListRows(newListRows);
    // The household's list has ARRIVED — and only here, in the same synchronous
    // block as setListRows so the two batch into one render. Setting the flag
    // earlier in this function (before the awaited contributors fetch) rendered
    // ready = true with listRows still [] and spent the once-only landing effect
    // on Browse (dev, 2026-09-12). Guarded on the household so a late tick from
    // a previous household (during a switch) can't declare the new one ready.
    const readyFor = householdRef.current?.id;
    console.debug("[householdReady] set attempt", { householdId, current: readyFor, rows: newListRows.length, willSet: readyFor === householdId });
    if (readyFor === householdId) setHouseholdReady(true);
    // Bought-set fingerprint for the tray attribution (status is NOT in the
    // provenance fingerprint above — a check changes no quantity).
    const boughtIds = items.filter(i => i.status === "bought").map(i => i.id).sort();
    const boughtFingerprint = boughtIds.join(",");
    if (boughtFingerprint !== boughtFingerprintRef.current) {
      boughtFingerprintRef.current = boughtFingerprint;
      checkedByRetryRef.current = 5;
      refreshCheckedBy(db, householdId, boughtIds);
    } else if (checkedByRetryRef.current > 0) {
      refreshCheckedBy(db, householdId, boughtIds);
    }
    // Preserve optimistic values for items with an in-flight write; commit the
    // rest from the server. Prevents the poll from clobbering a not-yet-
    // committed local edit.
    setQuantities((prev) => {
      if (pendingQtyRef.current.size === 0) return newQty;
      const merged = { ...newQty };
      pendingQtyRef.current.forEach((name) => {
        if (name in prev) merged[name] = prev[name];
      });
      return merged;
    });
    setChecked((prev) => {
      if (pendingCheckRef.current.size === 0) return newChecked;
      const merged = { ...newChecked };
      pendingCheckRef.current.forEach((name) => {
        if (name in prev) merged[name] = prev[name];
      });
      return merged;
    });
    setPrices(mergedPrices);
    setAddedByMap(newAddedBy);
    setContributorsMap(newContributors);

    // Only now, and only if something actually moved. Fired after the setters
    // so provenance is read against the list the user is about to see.
    if (listChanged) onListChangedRef.current();
  }

  async function loadActiveCycle(db, householdId) {
    const { data, error: cycleErr } = await db
      .from("provision_cycles")
      .select("id, cycle_type, label, started_at, seeded_from")
      .eq("household_id", householdId)
      .is("closed_at", null)
      .order("started_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (cycleErr) { console.error("loadActiveCycle error:", cycleErr.message); return null; }
    setActiveCycle(data || null);
    activeCycleRef.current = data || null;
    return data || null;
  }

  // Poll-tick refresh of the open cycle (2026-09-12). Two devices on one
  // household: the first action on device A opens the cycle; device B still
  // holds cycle_id = null until something re-reads it, and every event B
  // writes meanwhile is unattributed. Reads one indexed row per tick and only
  // touches state when the id actually changed. Skipped while wrap-up is
  // transitioning the cycle itself.
  const cyclePollBusyRef = useRef(false);
  async function pollActiveCycle(db, householdId) {
    if (wrappingUpRef.current || cyclePollBusyRef.current) return;
    cyclePollBusyRef.current = true;
    try {
      const { data, error: cycleErr, status: cycleStatus } = await db
        .from("provision_cycles")
        .select("id, cycle_type, label, started_at, seeded_from")
        .eq("household_id", householdId)
        .is("closed_at", null)
        .order("started_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cycleErr) { stopOnAuthFailure(cycleErr, cycleStatus); return; }
      const next = data || null;
      if ((next?.id || null) !== (activeCycleRef.current?.id || null)) {
        activeCycleRef.current = next;
        setActiveCycle(next);
      }
    } finally {
      cyclePollBusyRef.current = false;
    }
  }

  // The ONE open-cycle path. `uq_open_cycle_per_household` is correct: when two
  // devices race, exactly one insert wins. The loser gets 23505 — that is not
  // an error to show, it is the answer: select the household's open cycle,
  // adopt its id, and carry on with the action. Any other failure returns null
  // and the caller decides whether it is worth a toast.
  const ensureOpenCycle = useCallback(async (type = "planned") => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!hh || !db) return null;
    if (activeCycleRef.current) return activeCycleRef.current;
    const { data: newCycle, error: cycleErr } = await db
      .from("provision_cycles")
      .insert({
        household_id: hh.id,
        cycle_type: type,
        created_by: internalUserIdRef.current,
      })
      .select()
      .single();
    if (!cycleErr && newCycle) {
      activeCycleRef.current = newCycle;
      setActiveCycle(newCycle);
      return newCycle;
    }
    if (cycleErr?.code === "23505") {
      const adopted = await loadActiveCycle(db, hh.id);
      if (adopted) return adopted;
    }
    console.warn("ensureOpenCycle:", cycleErr?.message || "no cycle returned");
    return null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect 1 — session setup (keyed on user identity).
  // Creates the Supabase client once and runs bootstrap_new_user.
  // Does NOT fetch any household-scoped data — that belongs to Effect 2.
  useEffect(() => {
    // D4 / D1 (SPEC_auth_state_ui_gating.md): no session → no data. The anon
    // storefront preview that used to live here (seed catalog + category averages
    // over apikey-only REST, for a signed-out Browse) was retired 2026-09-24 with
    // Browse's signed-out rendering. After this the app makes ZERO anon queries
    // through THIS client; Home carries the signed-out surface. The one exception
    // (Phase A, 2026-10-29) is src/lib/supabaseAnon.js — a separate apikey-only client
    // used by the signed-out gift card alone, for exactly two calls the server grants
    // to anon on purpose (preview_meal_gift_public, a gift-photos signed URL). A test
    // greps src/ so nothing else can import it.
    if (!getTokenRef.current || !userId || !clerkId) {
      setLoading(false);
      return;
    }

    // A2: the hold counter belongs to THIS identity+session. A different person
    // (or the same person's next session) starts at zero.
    const holdKey = `${userId}|${clerkId}|${sessionId || ""}`;
    if (bootstrapHoldRef.current.key !== holdKey) bootstrapHoldRef.current = { key: holdKey, failures: 0 };
    let cancelled = false;
    let cancelHold = null;

    async function setupSession() {
      // The overlay ("Loading your provisions…") shows for the first attempt
      // only. A retry runs quietly behind the pill — flashing a full-screen
      // overlay every backoff step would be its own failure.
      if (bootstrapHoldRef.current.failures === 0) { setLoading(true); setError(null); }

      try {
        // Create Supabase client once — guard prevents stacking GoTrueClient instances on switch
        if (!supabaseRef.current) {
          supabaseRef.current = createSupabaseClient(getTokenRef.current, "op-provisions");
        }
        const db = supabaseRef.current;

        // Check for pending invite before bootstrapping. The code is read from the key
        // src/lib/preRum.js held at app entry — never from the URL, which preRum has
        // already cleaned before Splunk started (S29 / S33; Amendment 2026-10-26 R3).
        // The held copy survives Clerk's sign-up redirect for brand-new users.
        const pendingInviteCode = sessionStorage.getItem("pending_invite_code");

        // Same bridge for the referral code (spec D7). It attributes but never enrolls,
        // so it takes no part in any branch below — it is handed to the server, written
        // once at signup, and forgotten.
        const pendingRefCode = sessionStorage.getItem("pending_ref_code");

        // Use bootstrap_new_user RPC (SECURITY DEFINER — bypasses RLS).
        // Pass invite code so joining happens in one atomic transaction.
        // Identity is NOT passed: the function derives the Clerk id from
        // auth.jwt()->>'sub' server-side (migration 036, Authorization Part 2).
        // Sending p_clerk_id here would not just be redundant, it would fail —
        // no overload accepts it after 037.
        //
        // p_ref_code is passed ONLY when a code is actually present (migration 042).
        // That conditional is load-bearing, not tidiness: PostgREST resolves the overload
        // from the argument names supplied, so omitting the key keeps every ordinary
        // sign-in on the exact 3-named-arg call that works against an un-migrated
        // database. Without it, deploying this bundle before 042 lands would PGRST202
        // every session setup — a full auth-path outage. With it, only a ?ref= arrival
        // depends on the migration, which is the documented regression guard.
        const bootstrapArgs = {
          p_email: email,
          p_invite_code: pendingInviteCode || null,
          p_full_name: fullName || null,
        };
        if (pendingRefCode) bootstrapArgs.p_ref_code = pendingRefCode;

        const { data: bootstrapData, error: bootstrapErr, status: bootstrapStatus } = await db
          .rpc("bootstrap_new_user", bootstrapArgs);

        if (bootstrapErr) {
          // A2 (Amendment 2026-09-27): bootstrap is never terminal on a
          // transient failure. Until tonight this threw on ANY error and the
          // effect never re-ran (its deps are identity and session), so one
          // "TypeError: Load failed" on a cold iPhone radio left the person
          // signed in with no household until a cold start — no catalog, no
          // list, an empty Members zone, "Provisions". Now:
          //   transient (the request never got an answer) or 'missing' (the
          //   wrapper refused on a null token — Clerk must be asked again) →
          //   report to the pill, no toast, retry with backoff (1/2/4/8/16s,
          //   then 30s) and at once on `online` / visible.
          //   anything the server actually said ('real', or 401/403 with a
          //   token) → today's toast, as before.
          const authCls = classifyAuthFailure(bootstrapErr, bootstrapStatus);
          const holdable = authCls === 'missing' || (authCls === null && classifyFetchError(bootstrapErr) === 'transient');
          if (holdable) {
            if (cancelled) return;
            bootstrapHoldRef.current.failures += 1;
            console.warn(`bootstrap hold (${bootstrapHoldRef.current.failures}):`, bootstrapErr.message);
            reportTransientFailure();
            setLoading(false);
            cancelHold = scheduleHoldRetry(bootstrapHoldRef.current.failures, () => setBootstrapAttempt((a) => a + 1));
            return;
          }
          throw new Error(`Bootstrap failed: ${bootstrapErr.message}`);
        }
        bootstrapHoldRef.current.failures = 0;

        // Code has been attempted — clear the persisted copy so it can't re-trigger
        // on a future visit, regardless of whether the join actually succeeded.
        sessionStorage.removeItem("pending_invite_code");
        sessionStorage.removeItem("pending_ref_code");
        // The failed-invite flag (set below) is bounded to THIS bootstrap: cleared here
        // first, so a stale one can never reach a later sign-up in the same tab.
        sessionStorage.removeItem("invite_failed");

        internalUserIdRef.current = bootstrapData.user_id;
        clerkIdRef.current = clerkId;

        // Flag for the join banner. (The URL carries no code to clear — preRum removed
        // it before Splunk started; the held key is cleared above.)
        if (bootstrapData.joined_via_invite) {
          sessionStorage.setItem("just_joined_household", bootstrapData.household_name || "the place");
          sessionStorage.setItem("just_joined_household_id", bootstrapData.household_id);
        } else if (pendingInviteCode) {
          // A code was supplied but bootstrap did not join via it. bootstrap resolves
          // every VALID invite (new and existing users alike), so a false here means the
          // invite did not resolve — surface WHY rather than loading silently. This
          // regressed when the pre-flight lookup was removed: the pre-030 client showed
          // "Invite not found or already used." on this path; without this it is silent.
          // join_household is the single source of the three distinct messages
          // ("Invite not found." / "…already used." / "…expired."). The held key is
          // already cleared above, so a reload cannot re-fire the attempt.
          // NOTE: this is a LIVE join_household(text) call — it REQUIRES migration 030 on
          // the target database (dev has it; prod must get 030 before/with this client).
          const { data: joinResult, error: joinErr } = await db.rpc("join_household", {
            p_invite_code: pendingInviteCode,
          });
          if (joinErr) {
            // One message per failed invite (src/lib/inviteFailure.js): a brand-new user
            // gets the welcome sheet's fixed line (the sheet reads and burns this flag on
            // open — R3 ruling (a), 2026-10-26; never the code itself) and no toast; an
            // existing member, who never sees the sheet, keeps the toast.
            const notice = inviteFailureNotice(bootstrapData.created_household);
            if (notice.toast) setError(joinErr.message);
            if (notice.welcomeLine) sessionStorage.setItem("invite_failed", "1");
          } else if (joinResult?.household_id) {
            // Edge: join_household completed a join bootstrap didn't (e.g. the existing-
            // member no-op short-circuit). Route it through the same banner/switch flags.
            sessionStorage.setItem("just_joined_household", joinResult.household_name || "the place");
            sessionStorage.setItem("just_joined_household_id", joinResult.household_id);
          }
        }

        // Referral code for the Share-the-app verb (D9). Absent against an un-migrated
        // database — see the referralCode declaration for why that degrades safely.
        setReferralCode(bootstrapData.referral_code || null);

        // The create-vs-join seam (spec §3). `created_household` is true ONLY when
        // bootstrap minted a fresh solo place, so this is the one moment the welcome
        // sheet may fire. Deliberately NOT inferred from the household name: an existing
        // user who never renamed their place looks identical by name, and firing the
        // sheet at them is verification item 10's explicit failure.
        //
        // Reading a key an un-migrated database does not return yields undefined, so the
        // flag is never set and the sheet stays dormant. That is the intended degradation
        // — dormant, never wrong.
        if (bootstrapData.created_household === true) {
          sessionStorage.setItem("just_signed_up", "1");
        }

        // Stash bootstrap's chosen household as the fallback for Effect 2
        bootstrapHouseholdIdRef.current = bootstrapData.household_id;
        justJoinedViaInviteRef.current = bootstrapData.joined_via_invite === true; // no longer read in resolver — safe to clean up

        // Signal Effect 2 that session setup is complete
        bootstrappedRef.current = true;
        setBootstrapped(true);

      } catch (err) {
        console.error("Bootstrap error:", err.message);
        setError(err.message);
        setLoading(false);  // clear loading only on failure; Effect 2 owns the success signal
      }
    }

    setupSession();
    return () => {
      cancelled = true;
      if (cancelHold) cancelHold();
    };
    // fullName intentionally excluded: it is a cosmetic attribute that feeds
    // bootstrap_new_user (no-op for existing users) only. Including it caused a
    // name edit (which writes Clerk → changes fullName) to re-fire session
    // bootstrap and wedge the loading state. Bootstrap re-runs on identity
    // change — and, since the sign-out reset (below), on SESSION change: the
    // same person signing back in after a loss has the same Clerk id, and
    // without sessionId here nothing would re-bootstrap. bootstrapAttempt is
    // the A2 retry: bumped by the hold scheduler, nothing else.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, clerkId, email, sessionId, bootstrapAttempt]);

  // ── Sign-out reset (SPEC_auth_state_ui_gating.md §Sign-out reset, D3) ──
  // The missing half of sign-out. Until 2026-09-24 nothing here reset when the
  // session went away: supabaseRef, householdRef and `household` survived, the
  // meal poll kept ticking on household?.id, and the fetch wrapper sent
  // apikey-only requests — PostgREST saw anon, 401 every 2s, for four minutes.
  // Now every transition OUT of a live session (Clerk signed out, deliberate or
  // not; or the token gone LOST_STREAK ticks running) clears the household
  // world in one place. Effect 2's cleanup has already stopped its intervals
  // by the time this runs (userId went undefined), and App.js's meal poll dies
  // with household?.id. The catalog map goes too (D4a retired Browse's
  // signed-out preview, so nothing renders it without a session; Effect 2
  // rebuilds it on the next household load). The remembered active place (A6: per user,
  // `activeHouseholdId:<clerkId>`) stays, validated on the next sign-in.
  const wasLiveRef = useRef(false);
  useEffect(() => {
    if (sessionLive) { wasLiveRef.current = true; return; }
    if (!wasLiveRef.current) return;
    wasLiveRef.current = false;
    supabaseRef.current = null;
    householdRef.current = null;
    setHousehold(null);
    setHouseholdMembers([]);
    householdMembersRef.current = [];
    setMembersLoadedFor(null);
    setCatalogLoadedFor(null);
    bootstrappedRef.current = false;
    setBootstrapped(false);
    bootstrapHouseholdIdRef.current = null;
    bootstrapHoldRef.current = { key: null, failures: 0 };
    householdHoldRef.current = { key: null, failures: 0 };
    internalUserIdRef.current = null;
    setReferralCode(null);
    setHouseholdReady(false);
    listFingerprintRef.current = "";
    listRowsRef.current = [];
    setListRows([]);
    setQuantities({});
    setChecked({});
    setAddedByMap({});
    setContributorsMap({});
    setHiddenCatalogItems([]);
    hiddenCatalogItemsRef.current = [];
    hiddenIdsRef.current = new Set();
    setCatalogMap({});
    catalogRef.current = {};
    setPrices({});
    setCategoryAvgPrices({});
    setActiveCycle(null);
    activeCycleRef.current = null;
    setActiveSession(null);
    activeSessionRef.current = null;
    setPartnerSession(null);
    setStoreSuggestions([]);
    setCheckedByMap({});
    boughtFingerprintRef.current = "";
    setPlacements({});
    placementsRef.current = {};
    setCooks({});
    setCookIdsByMeal({});
    setCooksLoadedFor(null);
    errorSourceRef.current = null;
    setError(null);
    setLoading(false);
  }, [sessionLive]);

  // Effect 1b — Reconcile users.full_name from Clerk on each session.
  // WHY its own effect: bootstrap runs once and is a no-op for existing users, and
  // deliberately excludes fullName from its deps (feeding it there wedged loading).
  // So the name that arrives from Clerk *after* first bootstrap never persists.
  // This effect closes that gap: idempotent, own dep on fullName, only writes on a
  // real change. Does NOT go through bootstrap_new_user — it stays a targeted
  // name write. (This previously also avoided that function's 4-overload
  // ambiguity; migrations 036/037 collapse it to a single 3-arg signature, so
  // that particular hazard is gone — but the separation still stands on its
  // own merits, per the dependency reasoning above.)
  const lastSyncedNameRef = useRef(null);
  useEffect(() => {
    const db = supabaseRef.current;
    const name = (fullName || "").trim();
    if (!db) return;
    if (!bootstrappedRef.current || !internalUserIdRef.current) return; // need our user id
    if (!name) return;                        // Clerk has no name yet — nothing to write
    if (lastSyncedNameRef.current === name) return; // already reconciled this value

    (async () => {
      try {
        // Only write if stored value actually differs (avoid needless writes/realtime churn)
        const { data: existing } = await db
          .from("users")
          .select("full_name")
          .eq("id", internalUserIdRef.current)
          .maybeSingle();
        if (existing && existing.full_name === name) {
          lastSyncedNameRef.current = name;
          return;
        }
        const { error } = await db
          .from("users")
          .update({ full_name: name })
          .eq("id", internalUserIdRef.current);
        if (error) throw error;
        lastSyncedNameRef.current = name;
      } catch (err) {
        console.error("full_name reconcile error:", err.message);
        // Non-fatal: attribution degrades to email prefix, never blocks the app.
      }
    })();
  }, [fullName, bootstrapped]); // fires when Clerk name arrives or changes, post-bootstrap

  // Effect 2 — household-scoped loads (keyed on the resolved active household).
  // Reruns whenever activeHouseholdId changes (household switch). Never re-creates
  // the Supabase client — reads the one Effect 1 placed on supabaseRef.
  useEffect(() => {
    const db = supabaseRef.current;
    if (!db || !bootstrapped) return;               // wait for Effect 1
    if (!userId || !clerkId) return;                 // not signed in
    if (!sessionLive) return;                        // §Polling discipline: pollers run in `ready` only

    // ── Resolve which household to load ──
    const fallbackId = bootstrapHouseholdIdRef.current;

    let targetId;
    if (activeHouseholdId) {
      // Trust the context-validated active household in all cases. On auto-switch
      // joins, App.js has already called switchHousehold(joinedId), so this equals
      // the joined household. On silent joins, this stays on the prior household —
      // which is exactly what "silent" means: the view must not move. The old
      // justJoinedViaInvite forced-fallback overrode this and loaded the joined
      // household's data even for silent joins, causing a highlight/data split.
      targetId = activeHouseholdId;
    } else {
      // No active context yet (fresh device, pre-restore): bootstrap fallback.
      targetId = fallbackId;
    }
    if (!targetId) return;

    // A3: the household-fetch hold counter belongs to THIS household.
    if (householdHoldRef.current.key !== targetId) householdHoldRef.current = { key: targetId, failures: 0 };

    let pollInterval;
    let catalogPollInterval;
    let onlineTick;
    let cancelHold = null;
    let cancelled = false;

    async function loadForHousehold(householdId) {
      // Overlay on the first attempt only; a retry runs behind the pill (A3).
      if (householdHoldRef.current.failures === 0) setLoading(true);
      setHouseholdReady(false);

      // Reset per-household state so the previous household's rows don't flash
      listFingerprintRef.current = "";
      setListRows([]);
      setQuantities({});
      setChecked({});
      setAddedByMap({});
      setContributorsMap({});
      setActiveCycle(null);
      activeCycleRef.current = null;
      setActiveSession(null);
      activeSessionRef.current = null;
      setPartnerSession(null);
      setStoreSuggestions([]);
      setCheckedByMap({});
      boughtFingerprintRef.current = "";
      setPlacements({});
      placementsRef.current = {};
      setCooks({});
      setCookIdsByMeal({});
      setCooksLoadedFor(null);

      try {
        // Fetch the household record. Banner columns (migration 024) are read
        // best-effort: if 024 hasn't been applied to this environment yet, the
        // extended select errors on the missing column, and we fall back to the
        // base columns so the app degrades to today's espresso header instead of
        // hard-erroring. Once 024 is applied, the extended select succeeds.
        const BANNER_COLS = "photo_path, photo_position_x, photo_position_y, photo_zoom, banner_wordmark, created_by";
        let hhData = null, hhErr = null, hhStatus = null;
        {
          const ext = await db
            .from("households")
            .select(`id, name, budget_goal, ${BANNER_COLS}`)
            .eq("id", householdId)
            .single();
          if (ext.error) {
            // Retry with base columns — distinguishes "migration not applied" (retry
            // succeeds) from a genuine fetch failure (retry also errors).
            const base = await db
              .from("households")
              .select("id, name, budget_goal")
              .eq("id", householdId)
              .single();
            hhData = base.data; hhErr = base.error; hhStatus = base.status;
          } else {
            hhData = ext.data; hhErr = ext.error; hhStatus = ext.status;
          }
        }
        if (hhErr) {
          // A3 (Amendment 2026-09-27): this early return was the second terminal
          // exit — no polls, no `online` listener, nothing to re-run it. A
          // transient failure (or the wrapper's refusal on a null token) now
          // holds and retries exactly as bootstrap does; a real error toasts.
          if (cancelled) return;
          const authCls = classifyAuthFailure(hhErr, hhStatus);
          const holdable = authCls === 'missing' || (authCls === null && classifyFetchError(hhErr) === 'transient');
          if (holdable) {
            householdHoldRef.current.failures += 1;
            console.warn(`household fetch hold (${householdHoldRef.current.failures}):`, hhErr.message);
            reportTransientFailure();
            setLoading(false);
            cancelHold = scheduleHoldRetry(householdHoldRef.current.failures, () => setHouseholdAttempt((a) => a + 1));
            return;
          }
          setError(`Could not fetch place: ${hhErr.message}`);
          setLoading(false); return;
        }
        householdHoldRef.current.failures = 0;
        if (cancelled) return;
        const hh = hhData;
        // Private bucket → resolve photo_path to a signed URL for the header.
        // Best-effort: a resolve failure just leaves the espresso header.
        if (hh.photo_path) {
          try {
            const { data: signed } = await db.storage
              .from("household-photos")
              .createSignedUrl(hh.photo_path, 60 * 60);
            if (cancelled) return;
            hh.photoUrl = signed?.signedUrl || null;
          } catch (_e) { hh.photoUrl = null; }
        }
        setHousehold(hh);
        householdRef.current = hh;

        // Members
        const { data: members } = await db
          .from("household_members")
          .select("id, user_id, role")
          .eq("household_id", hh.id)
          .is("deleted_at", null);
        if (cancelled) return;

        const { data: profiles } = await db
          .rpc("get_household_member_profiles", { p_household_id: hh.id });
        if (cancelled) return;

        const membersWithProfiles = (members || []).map(m => ({
          ...m,
          users: profiles?.find(p => p.user_id === m.user_id) || null
        }));
        setHouseholdMembers(membersWithProfiles);
        householdMembersRef.current = membersWithProfiles;
        setMembersLoadedFor(hh.id);

        // Cook log (058) — with the household. The Plan poll re-reads it when a
        // placement's cooked_at changes (loadPlacements); nothing else polls it.
        await loadCooks(db, hh);
        if (cancelled) return;

        // Open shopping sessions — mine adopted (or expired), partner's noted.
        await loadSessions(db, hh.id);
        if (cancelled) return;

        // Hidden items (per-user; reload on switch to pick up any hides from the other household view)
        const { data: hiddenRows } = await db
          .from("user_hidden_items")
          .select("catalog_item_id, catalog_items(id, name, category, is_global, price_hint, created_by)")
          .eq("clerk_id", clerkIdRef.current);
        if (cancelled) return;

        const hiddenIds = new Set((hiddenRows || []).map(h => h.catalog_item_id));
        hiddenIdsRef.current = hiddenIds;
        const hiddenCatalogList = (hiddenRows || []).map(h => h.catalog_items).filter(Boolean);
        hiddenCatalogItemsRef.current = hiddenCatalogList;
        setHiddenCatalogItems(hiddenCatalogList);

        // Global catalog — the shared seed set (~50 rows) that ALWAYS exists.
        // On a cold start the first read can return empty (or transiently error)
        // before the session/RLS is warm; proceeding then would strand
        // catalogMap={} with loading already false, so Browse renders a blank
        // void until something forces a refetch — the "empty until you tap a
        // filter" cold-start bug (a filter can't add rows; the data was simply
        // missing). An empty global catalog is never legitimate, so retry with a
        // short backoff before giving up.
        let catalog = null;
        let catalogErr = null;
        for (let attempt = 0; attempt < 4; attempt++) {
          ({ data: catalog, error: catalogErr } = await db
            .from("catalog_items")
            .select("id, name, category, unit, is_global, price_hint, is_staple")
            .eq("is_global", true)
            .is("deleted_at", null));
          if (cancelled) return;
          if (!catalogErr && catalog && catalog.length > 0) break;
          await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
          if (cancelled) return;
        }
        if (catalogErr) { setError(`Could not load catalog: ${catalogErr.message}`); setLoading(false); return; }

        // Custom catalog (household-scoped)
        const { data: customItems, error: customErr } = await db
          .from("catalog_items")
          .select("id, name, category, unit, is_global, price_hint, is_staple, created_by")
          .eq("household_id", hh.id)
          .eq("is_global", false)
          .is("deleted_at", null);
        if (customErr) { setError(`Could not load custom items: ${customErr.message}`); setLoading(false); return; }
        if (cancelled) return;

        // Per-household staples (row-presence). is_staple is stamped from this
        // set, NOT the shared catalog_items column (migration 016).
        const stapleSet = await fetchStapleSet(db, hh.id);
        if (cancelled) return;

        const cMap = {};
        (catalog || []).forEach((item) => {
          if (!hiddenIds.has(item.id)) cMap[item.name] = { ...item, is_staple: stapleSet.has(item.id), created_by: null };
        });
        (customItems || []).forEach((item) => {
          if (!hiddenIds.has(item.id)) cMap[item.name] = { ...item, is_staple: stapleSet.has(item.id), created_by: item.created_by ?? "custom" };
        });
        setCatalogMap(cMap);
        catalogRef.current = cMap;
        setCatalogLoadedFor(hh.id);
        const hintPrices = {};
        Object.values(cMap).forEach(item => {
          if (item.price_hint != null) hintPrices[item.name] = parseFloat(item.price_hint);
        });
        setPrices(hintPrices);

        // Category avg prices
        const { data: avgRows, error: avgErr } = await db
          .from("category_avg_prices")
          .select("category, avg_price");
        if (!avgErr && avgRows) {
          if (cancelled) return;
          const avgMap = {};
          avgRows.forEach(row => { avgMap[row.category] = parseFloat(row.avg_price); });
          setCategoryAvgPrices(avgMap);
        }

        if (cancelled) return;
        await loadListItems(db, hh.id);
        if (cancelled) return;
        await loadActiveCycle(db, hh.id);
        if (cancelled) return;

        // Amendment 2026-09-24 (offline is not auth loss): every tick passes
        // isPollingOpen — closed while navigator.onLine is false or for the
        // hold after a 401/403 — so nothing is sent into the void, and the
        // `online` event runs one immediate tick instead of waiting out the
        // interval. A missing token does not close the gate: the tick has to
        // reach the wrapper for Clerk to be asked again.
        const listTick = () => { if (!isPollingOpen()) return; loadListItems(db, hh.id); pollActiveCycle(db, hh.id); };
        const catalogTick = () => { if (!isPollingOpen()) return; refreshCatalogRef.current(); };
        pollInterval = setInterval(listTick, 2000);
        catalogPollInterval = setInterval(catalogTick, 20000);
        onlineTick = () => { listTick(); catalogTick(); };
        window.addEventListener("online", onlineTick);

        reportSuccess();
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          console.error("loadForHousehold error:", err.message);
          setError(err.message);
          setLoading(false);
        }
      }
    }

    loadForHousehold(targetId);

    return () => {
      cancelled = true;
      if (cancelHold) cancelHold();
      if (pollInterval) clearInterval(pollInterval);
      if (catalogPollInterval) clearInterval(catalogPollInterval);
      if (onlineTick) window.removeEventListener("online", onlineTick);
      if (realtimeChannelRef.current) {
        try { supabaseRef.current?.removeChannel(realtimeChannelRef.current); } catch (e) {}
        realtimeChannelRef.current = null;
      }
    };
    // householdAttempt is the A3 retry: bumped by the hold scheduler, nothing else.
  }, [activeHouseholdId, userId, clerkId, bootstrapped, sessionLive, householdAttempt]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────
  // updateQty: handles both global catalog items AND custom items.
  // If the item isn't in the catalog yet, it's inserted first as
  // a household-specific item, then the list_items row is written.
  // ─────────────────────────────────────────────────────────────
  const updateQty = useCallback(async (itemName, qty, categoryName, price) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!hh || !db) return;

    // Resolved ids of the row this write touched — returned so an in-store add
    // can record its `added_in_store` event after the write commits (D12).
    // Null for removals and on failure.
    let touched = null;
    // Optimistic key: the live row's id when one exists, else pre:<catalog id>
    // until the insert lands. A brand-new NAME has no catalog id yet, so it
    // gets no optimistic value — nothing renders it before catalogMap updates.
    let optKey = null;
    const setOpt = (key, value) => {
      if (!key) return;
      pendingQtyRef.current.add(key);
      setQuantities((prev) => ({ ...prev, [key]: Math.max(0, value) }));
    };
    const clearPending = () => { if (optKey) pendingQtyRef.current.delete(optKey); };

    try {
      // Look up in current catalogRef (includes custom items added this session)
      let catalogItem = catalogRef.current[itemName];
      if (catalogItem) { optKey = liveRowIdFor(catalogItem.id) || preKey(catalogItem.id); setOpt(optKey, qty); }

      // If not found, this is a brand-new custom item — insert it into catalog_items
      if (!catalogItem) {
        // Resolver hardening (F1b Layer 2): a HIDDEN item is evicted from catalogRef,
        // so it looks "new" here. Before inserting a fork, resolve an exact-normalized
        // name match against the hidden set to the EXISTING catalog row. (F0's DB index
        // uq_live_list_item would reject a duplicate live row anyway; this keeps the
        // path a clean reuse rather than surfacing as an error.)
        const norm = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");
        const hiddenMatch = hiddenCatalogItemsRef.current.find(it => norm(it.name) === norm(itemName));
        if (hiddenMatch) {
          catalogItem = hiddenMatch;
          catalogRef.current = { ...catalogRef.current, [itemName]: hiddenMatch };
        } else {
          const category = categoryName || "Household";
          const { data: insertedId, error: insertErr } = await db
            .rpc("insert_custom_catalog_item", {
              p_name: itemName,
              p_category: category,
              p_household_id: hh.id,
              p_created_by: internalUserIdRef.current,
            });
          if (insertErr) throw insertErr;
          catalogItem = { id: insertedId, name: itemName, category: category, is_global: false, household_id: hh.id };
          // Keep catalogRef and catalogMap in sync
          catalogRef.current = { ...catalogRef.current, [itemName]: catalogItem };
          setCatalogMap((prev) => ({ ...prev, [itemName]: catalogItem }));
        }
      }
      if (!optKey) { optKey = liveRowIdFor(catalogItem.id) || preKey(catalogItem.id); setOpt(optKey, qty); }

      if (qty <= 0) {
        // Atomic remove: soft-delete the list_items row AND clear its
        // contributor badges in one transaction (migration 009). Replaces the
        // old client-side soft-delete that left contributors behind and caused
        // badge-resurrection on re-add (008 upsert revives the same row).
        const { error: delErr } = await db.rpc("remove_list_item", {
          p_household_id: hh.id,
          p_catalog_item_id: catalogItem.id,
        });
        if (delErr) throw delErr;
        // The row is gone: drop its entry (and any pre-row placeholder) now rather
        // than waiting for the poll.
        setQuantities((prev) => { const n = { ...prev }; delete n[optKey]; delete n[preKey(catalogItem.id)]; return n; });
      } else {
        // DXA: was this catalog item LIVE on the list before this write? The
        // UPDATE below has no deleted_at filter (it revives tombstones), so a
        // matched row is either a live-row bump (silent) or a tombstone revive
        // (an add — the item re-enters the list). Read from listRowsRef, which
        // is server state, not the optimistic map. Edge: a row inserted this
        // session and bumped before the 2s poll delivers it reads as not-live
        // and emits twice.
        const hadLiveRow = !!liveRowIdFor(catalogItem.id);
        // One emitter for both add branches. source: 'browse' covers every
        // manual add — Browse's Add, search quick-add, the new-item form and
        // the Shop in-store Add sheet all funnel through updateQty. Telemetry
        // never throws into, or blocks, the add.
        const emitItemAdded = () => {
          try {
            tracer.startSpan("item_added_to_list", {
              attributes: {
                catalog_item_id: catalogItem.id,
                category: categoryName || catalogItem.category || null,
                is_custom: !catalogItem.is_global,
                household_id: hh.id,
                source: "browse",
              },
            }).end();
          } catch (e) {
            console.warn("[rum] item_added_to_list event failed:", e);
          }
        };
        const { data: updateData, error: updateErr } = await db
          .from("list_items")
          .update({ quantity: qty, status: "pending", deleted_at: null })
          .eq("household_id", hh.id)
          .eq("catalog_item_id", catalogItem.id)
          .select();
        if (updateErr) throw updateErr;

        if (!updateData || updateData.length === 0) {
          // Truly no row exists — insert fresh
          // Auto-open a planned cycle if none is active yet (adopts on a lost race)
          if (!activeCycleRef.current) await ensureOpenCycle("planned");

          const { data: newItemId, error: insertErr } = await db
            .rpc("insert_list_item", {
              p_household_id: hh.id,
              p_catalog_item_id: catalogItem.id,
              p_quantity: qty,
              p_status: "pending",
              p_added_by: internalUserIdRef.current,
              p_cycle_id: activeCycleRef.current?.id || null,
              p_price_per_unit: (price != null && price > 0) ? price : null,
            });
          if (insertErr) throw insertErr;
          const newItem = { id: newItemId };
          touched = { listItemId: newItemId, catalogItemId: catalogItem.id };
          // DXA event: a NEW row on the list. household_id is also stamped
          // globally (rum.js setHousehold); repeated so the event stands alone
          // in a DXA funnel.
          emitItemAdded();
          // Real row now exists: mirror the value under its id. The pre:<cid>
          // placeholder stays until the poll delivers the row (the reader falls
          // back to it meanwhile), then the rebuild drops it.
          setQuantities((prev) => ({ ...prev, [newItemId]: qty }));

          // Record this user as the first contributor
          if (newItem && internalUserIdRef.current) {
            await db
              .from("list_item_contributors")
              .upsert({
                list_item_id: newItem.id,
                user_id: internalUserIdRef.current,
                quantity_added: qty,
              }, { onConflict: "list_item_id,user_id" });
          }
        } else {
          // Row already exists — update this user's contributor quantity
          touched = { listItemId: updateData[0].id, catalogItemId: catalogItem.id };
          // DXA event: the UPDATE matched a TOMBSTONE and revived it — the item
          // re-entered the list, which is an add. A bump of a live row is not.
          // (Found 2026-09-20: without this, an item that had ever been on the
          // list never fired again — most adds in a household with history.)
          if (!hadLiveRow) emitItemAdded();
          setQuantities((prev) => ({ ...prev, [updateData[0].id]: qty }));
          if (internalUserIdRef.current && updateData?.[0]?.id) {
            await db
              .from("list_item_contributors")
              .upsert({
                list_item_id: updateData[0].id,
                user_id: internalUserIdRef.current,
                quantity_added: Math.max(1, qty),
              }, { onConflict: "list_item_id,user_id" });
          }
        }

      }
      clearPending();
      reportSuccess();
      return touched;
    } catch (err) {
      clearPending();
      console.error("updateQty error:", err.message, err);
      console.error("Item:", itemName, "Qty:", qty, "Catalog item:", catalogRef.current[itemName]);
      // Classify FIRST. On a transient (offline) failure, keep the optimistic
      // value — it's the last-good intent that reconnect will reconcile. Only
      // roll back on a genuine error (real rejection: bad data, RLS, etc.),
      // where the value truly didn't take.
      if (classifyFetchError(err) === 'transient') {
        reportTransientFailure();
      } else {
        setQuantities((prev) => { const n = { ...prev }; if (optKey) delete n[optKey]; return n; });
        setError(`Could not update quantity: ${err.message}`);
      }
      return null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // empty deps — uses refs, never stale

  const toggleChecked = useCallback(async (itemName, listItemId) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!hh || !db) return;

    // F2: write to the specific list_items row by its id (threaded from
    // listRows). Scoping by catalog_item_id flipped EVERY live row sharing
    // that catalog id (Bug 2: "check one, both check"). The id is unique, so
    // this can only ever touch the tapped row.
    if (!listItemId) { setError(`"${itemName}" is not on the list`); return; }

    // The tap's INTENT, read from the closure. Whether it is also a real
    // transition is decided by the server below, never here.
    const newStatus = checked[listItemId] ? "pending" : "bought";
    // F3: mark this row as having an in-flight write so the 2s poll won't
    // snap the optimistic value back to the stale server status before the
    // update commits (Bug 3: toggle bounces / needs ~3 taps to stick).
    pendingCheckRef.current.add(listItemId);
    setChecked((prev) => ({ ...prev, [listItemId]: !prev[listItemId] }));

    try {
      // SPEC_checked_event_duplicate_emission: the write is conditional on the
      // row NOT already being at newStatus, and returns the row it changed. So
      // the resolved status is a server-confirmed before/after transition — a
      // re-tap that lands on an already-committed write (the closure or a
      // late poll still showing the row unchecked) matches zero rows and
      // resolves to nothing, instead of re-deriving "bought" from stale state
      // and emitting a second `checked` event. A genuine uncheck → recheck is
      // two real transitions and still resolves both times.
      const { data: rows, error: updateErr } = await db
        .from("list_items")
        .update({ status: newStatus })
        .eq("id", listItemId)
        .eq("household_id", hh.id)
        .is("deleted_at", null)
        .neq("status", newStatus)
        .select("status");
      if (updateErr) throw updateErr;
      pendingCheckRef.current.delete(listItemId);
      reportSuccess();
      if (!rows || rows.length === 0) {
        // No transition: the row was already at newStatus (or is gone).
        // Reconcile the optimistic flip to the server's truth and resolve to
        // nothing, so the caller records no event. Read the truth rather than
        // assume it; if that read fails, the row already being at newStatus is
        // by far the likeliest reason for the zero-row update.
        let serverBought = newStatus === "bought";
        try {
          const { data: row } = await db
            .from("list_items")
            .select("status")
            .eq("id", listItemId)
            .is("deleted_at", null)
            .maybeSingle();
          if (row) serverBought = row.status === "bought";
        } catch (e) {
          console.warn("toggleChecked reconcile read failed:", e?.message || e);
        }
        setChecked((prev) => ({ ...prev, [listItemId]: serverBought }));
        return null;
      }
      const committedStatus = rows[0].status;
      // DXA event: a row checked off (bought), never the un-check. Same tracer
      // pattern as item_added_to_list; catalog_item_id may be null when the
      // name no longer resolves in catalogRef (hidden/evicted). Telemetry never
      // throws into, or blocks, the toggle.
      if (committedStatus === "bought") {
        try {
          tracer.startSpan("item_checked_off", {
            attributes: {
              list_item_id: listItemId,
              catalog_item_id: catalogRef.current[itemName]?.id ?? null,
              household_id: hh.id,
            },
          }).end();
        } catch (e) {
          console.warn("[rum] item_checked_off event failed:", e);
        }
      }
      // Resolves to the committed status so the Shop tab can write the matching
      // list_item_events row AFTER the primary write (D12) — and only when the
      // write actually transitioned the row. Nothing on failure or no-op.
      return committedStatus;
    } catch (err) {
      pendingCheckRef.current.delete(listItemId);
      console.error("toggleChecked error:", err.message);
      setChecked((prev) => ({ ...prev, [listItemId]: !prev[listItemId] }));
      if (classifyFetchError(err) === 'transient') { reportTransientFailure(); } else { setError(`Could not update item: ${err.message}`); }
      return null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked]);

  const clearAll = useCallback(async () => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!hh || !db) return;

    setQuantities({});
    setChecked({});

    try {
      const { error: clearErr } = await db
        .from("list_items")
        .update({ deleted_at: new Date().toISOString() })
        .eq("household_id", hh.id)
        .is("deleted_at", null);
      if (clearErr) throw clearErr;
    } catch (err) {
      console.error("clearAll error:", err.message);
      setError(`Could not clear list: ${err.message}`);
      await loadListItems(db, hh.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─────────────────────────────────────────────────────────────
  // PROVISION CYCLES & SESSIONS
  // ─────────────────────────────────────────────────────────────

  const openCycle = useCallback(async (type = "planned") => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!hh || !db) return null;

    // Don't open a second cycle if one is already active
    if (activeCycleRef.current) return activeCycleRef.current;

    const cycle = await ensureOpenCycle(type);
    if (!cycle) { setError("Could not open cycle"); return null; }
    return cycle;
  }, [ensureOpenCycle]);

  // `silent` — the in-store event path (recordListEvent) starts the session as
  // a side effect of a check that already succeeded; a failure there is logged,
  // never toasted (D12: telemetry may not slow or interrupt shopping).
  const startSession = useCallback(async ({ silent = false } = {}) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!hh || !db) return null;

    // Ensure there's an active cycle — create one if not
    let cycle = activeCycleRef.current;
    if (!cycle) cycle = await openCycle("planned");
    if (!cycle) return null;

    // Don't create a duplicate session for this user in this cycle
    if (activeSessionRef.current) return activeSessionRef.current;

    // Capture GPS silently (best effort — won't block if denied)
    let gpsLat = null;
    let gpsLng = null;
    if (navigator.geolocation) {
      try {
        const pos = await new Promise((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 4000 })
        );
        gpsLat = pos.coords.latitude;
        gpsLng = pos.coords.longitude;
      } catch {
        // GPS denied or timed out — that's fine, continue without it
      }
    }

    const { data: newSession, error: sessionErr } = await db
      .from("shopping_sessions")
      .insert({
        household_id: hh.id,
        cycle_id: cycle.id,
        user_id: internalUserIdRef.current,
        gps_lat: gpsLat,
        gps_lng: gpsLng,
      })
      .select()
      .single();

    if (sessionErr) {
      if (silent) console.warn("startSession:", sessionErr.message);
      else setError(`Could not start session: ${sessionErr.message}`);
      return null;
    }
    setActiveSession(newSession);
    activeSessionRef.current = newSession;
    eventSeqRef.current = 0;
    return newSession;
  }, [openCycle]);

  // ── In-store session lifecycle (SPEC_shop_lens_instore_capture.md) ──
  // Sessions are PER PERSON (D10). Expiry is 8h without Wrap up, checked
  // client-side on Shop mount and on each in-store action (D11) — there is no
  // cron. Only the caller's own session is ever ended here: a partner's stale
  // row is theirs (and 032's sessions_update_own would reject the write anyway).
  const SESSION_MAX_AGE_MS = 8 * 60 * 60 * 1000;
  const isStaleSession = (s) => !!s && (Date.now() - new Date(s.started_at).getTime()) > SESSION_MAX_AGE_MS;

  async function endSessionRow(db, sessionId) {
    const { error: endErr } = await db
      .from("shopping_sessions")
      .update({ ended_at: new Date().toISOString() })
      .eq("id", sessionId);
    if (endErr) console.warn("endSession:", endErr.message);
  }

  // Reads the household's OPEN sessions: mine (adopted as activeSession, or
  // ended if stale) and the first non-stale partner's (its store becomes the
  // prompt's first chip). Also refreshes the store-name suggestions. This is the
  // first code path that ever READS shopping_sessions from a client — the 032
  // policies were verified by inspection only until this shipped.
  async function loadSessions(db, householdId, { withStores = true } = {}) {
    const me = internalUserIdRef.current;
    const { data: open, error: openErr } = await db
      .from("shopping_sessions")
      .select("id, household_id, cycle_id, user_id, store_id, store_name_raw, started_at, ended_at, gps_lat, gps_lng")
      .eq("household_id", householdId)
      .is("ended_at", null)
      .order("started_at", { ascending: false });
    if (openErr) { console.warn("loadSessions:", openErr.message); return; }
    let mine = null;
    let partner = null;
    for (const s of open || []) {
      if (s.user_id === me) {
        if (isStaleSession(s)) { await endSessionRow(db, s.id); continue; }
        if (!mine) mine = s;
      } else if (!partner && !isStaleSession(s)) {
        partner = s;
      }
    }
    setActiveSession(mine);
    activeSessionRef.current = mine;
    setPartnerSession(partner);
    if (!withStores) return;

    const { data: named, error: namedErr } = await db
      .from("shopping_sessions")
      .select("store_name_raw, started_at")
      .eq("household_id", householdId)
      .not("store_name_raw", "is", null)
      .order("started_at", { ascending: false })
      .limit(50);
    if (namedErr) { console.warn("loadSessions stores:", namedErr.message); return; }
    const seen = new Set();
    const names = [];
    (named || []).forEach(r => {
      const n = (r.store_name_raw || "").trim();
      if (n && !seen.has(n.toLowerCase())) { seen.add(n.toLowerCase()); names.push(n); }
    });
    setStoreSuggestions(names);
  }

  const refreshSessions = useCallback(async () => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh || !internalUserIdRef.current) return;
    await loadSessions(db, hh.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The session for the current in-store action. Re-reads the household's
  // OPEN sessions on every action (one indexed select per tap): the D11 expiry
  // check then runs against started_at as the database holds it, not a cached
  // copy; a session this person opened on another device is adopted rather
  // than duplicated; and the partner's CURRENT store lands in the prompt.
  // loadSessions ends a stale own session itself, so what remains is either a
  // fresh session or none. Serialized through sessionStartingRef so two quick
  // taps cannot open two sessions during the GPS wait.
  const ensureSession = useCallback(async ({ silent = false } = {}) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh) return null;
    if (sessionStartingRef.current) return sessionStartingRef.current;
    sessionStartingRef.current = (async () => {
      await loadSessions(db, hh.id, { withStores: false });
      const cur = activeSessionRef.current;
      if (cur) return cur;
      return startSession({ silent });
    })();
    try {
      return await sessionStartingRef.current;
    } finally {
      sessionStartingRef.current = null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startSession]);

  // Writes store_name_raw on the caller's OWN open session (032:
  // sessions_update_own). Reads the row back rather than trusting the 2xx —
  // a write no policy admits matches zero rows and raises no error (041).
  //
  // 054 (SPEC_store_identity.md): the store is IDENTIFIED through resolve_store,
  // the only write path to stores / known_stores. It returns the household's
  // known_stores.id, which becomes shopping_sessions.store_id (session → link →
  // store, D2). The session's own captured GPS rides along; with none, the
  // resolver matches this household's history only. Identify, never describe:
  // no new UI, the prompt's copy and chips are untouched. Resolution never
  // blocks the shopper — on any failure the raw name is still written and the
  // trip is simply unanchored (053's Anchored leg reads no_store).
  const setSessionStore = useCallback(async (name) => {
    const db = supabaseRef.current;
    const cur = activeSessionRef.current;
    const clean = (name || "").trim();
    if (!db || !cur || !clean) return false;
    let storeId = null;
    try {
      const { data: resolved, error: resolveErr } = await db.rpc("resolve_store", {
        p_household_id: cur.household_id,
        p_name_raw: clean,
        p_lat: cur.gps_lat ?? null,
        p_lng: cur.gps_lng ?? null,
      });
      if (resolveErr) console.warn("resolve_store:", resolveErr.message);
      else storeId = resolved || null;
    } catch (e) {
      console.warn("resolve_store:", e?.message || e);
    }
    const patch = storeId ? { store_name_raw: clean, store_id: storeId } : { store_name_raw: clean };
    const { data, error: storeErr } = await db
      .from("shopping_sessions")
      .update(patch)
      .eq("id", cur.id)
      .select("id, household_id, cycle_id, user_id, store_id, store_name_raw, started_at, ended_at, gps_lat, gps_lng")
      .maybeSingle();
    if (storeErr || !data) {
      console.warn("setSessionStore:", storeErr?.message || "no row updated");
      return false;
    }
    setActiveSession(data);
    activeSessionRef.current = data;
    setStoreSuggestions(prev => [clean, ...prev.filter(n => n.toLowerCase() !== clean.toLowerCase())]);
    return true;
  }, []);

  // Best-effort event write (D12). Called AFTER the primary write has
  // committed; never awaited by the tap; never toasts. Starts the session on
  // the first in-store action of a trip (silently — a session failure must not
  // surface as a check failure). Against a database without 046 the insert
  // errors and is logged, nothing more.
  const recordListEvent = useCallback(async (eventType, { listItemId = null, catalogItemId } = {}) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh || !catalogItemId || !internalUserIdRef.current) return;
    try {
      const session = await ensureSession({ silent: true });
      eventSeqRef.current += 1;
      const { error: evErr } = await db
        .from("list_item_events")
        .insert({
          household_id: hh.id,
          list_item_id: listItemId,
          catalog_item_id: catalogItemId,
          user_id: internalUserIdRef.current,
          session_id: session?.id || null,
          cycle_id: activeCycleRef.current?.id || session?.cycle_id || null,
          event_type: eventType,
          sequence: eventSeqRef.current,
        });
      if (evErr) console.warn(`list_item_events ${eventType}:`, evErr.message);
    } catch (err) {
      console.warn(`list_item_events ${eventType}:`, err?.message || err);
    }
  }, [ensureSession]);

  // Returns true only when the trip actually closed. The caller shows a trip
  // summary on the strength of it, and a celebration over a failed wrap-up is
  // worse than none — the errors below are reported through setError, never
  // thrown, so a bare `await` could not tell the two apart.
  const wrapUpTrip = useCallback(async (rollItemNames = []) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!hh || !db) return false;

    wrappingUpRef.current = true;
    try {
      // If no active cycle, open (or adopt) one now so we have something to close
      let cycle = activeCycleRef.current;
      if (!cycle) {
        cycle = await ensureOpenCycle("planned");
        if (!cycle) { setError("Could not create cycle"); return false; }
      }

      // Close active session if one is open
      if (activeSessionRef.current) {
        await db
          .from("shopping_sessions")
          .update({ ended_at: new Date().toISOString() })
          .eq("id", activeSessionRef.current.id);
        setActiveSession(null);
        activeSessionRef.current = null;
      }

      // Resolve roll-forward item names → list_item IDs
      // NOTE: query without cycle_id filter to handle legacy items (cycle_id = null)
      let rollIds = [];
      if (rollItemNames.length > 0) {
        const { data: rollItems } = await db
          .from("list_items")
          .select("id, catalog_items(name)")
          .eq("household_id", hh.id)
          .eq("status", "pending")
          .is("deleted_at", null);

        rollIds = (rollItems || [])
          .filter(li => rollItemNames.includes(li.catalog_items?.name))
          .map(li => li.id);
      }

      await db.rpc("archive_trip_items", {
        p_household_id: hh.id,
        p_keep_item_ids: rollIds,
      });

      // Call close_cycle RPC — creates new cycle with rolled items
      const { data: newCycleId, error: closeErr } = await db
        .rpc("close_cycle", {
          p_cycle_id: cycle.id,
          p_roll_item_ids: rollIds,
        });

      if (closeErr) throw closeErr;

      // Update active cycle to the new one (or null if nothing rolled)
      if (newCycleId) {
        const { data: newCycle } = await db
          .from("provision_cycles")
          .select("id, cycle_type, label, started_at, seeded_from")
          .eq("id", newCycleId)
          .single();
        setActiveCycle(newCycle || null);
        activeCycleRef.current = newCycle || null;
      } else {
        setActiveCycle(null);
        activeCycleRef.current = null;
      }

      // Reset UI state and reload
      setChecked({});
      setQuantities({});
      wrappingUpRef.current = false;
      await loadListItems(db, hh.id);
      return true;

    } catch (err) {
      wrappingUpRef.current = false;
      console.error("wrapUpTrip error:", err.message);
      setError(`Could not wrap up trip: ${err.message}`);
      return false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Trip reality (SPEC_trip_qualification_v2 D8) ──
  // The client DISPLAYS a verdict; it never decides one. get_trip_reality is the
  // only read path (it also stamps the ask as shown, once, and says so with
  // first_ask); answer_trip_reality is the only write path. Both are best-effort
  // from the summary: a failure shows the plain summary (state 1), never a
  // toast — the same posture as recordListEvent (D12).
  const fetchTripReality = useCallback(async (sessionId) => {
    const db = supabaseRef.current;
    if (!db || !sessionId) return null;
    try {
      const { data, error } = await db.rpc("get_trip_reality", { p_session_id: sessionId });
      if (error) { console.warn("get_trip_reality:", error.message); return null; }
      return data || null;
    } catch (err) {
      console.warn("get_trip_reality:", err?.message || err);
      return null;
    }
  }, []);

  const answerTripReality = useCallback(async (sessionId, answer) => {
    const db = supabaseRef.current;
    if (!db || !sessionId) return null;
    try {
      const { data, error } = await db.rpc("answer_trip_reality", { p_session_id: sessionId, p_answer: answer });
      if (error) { console.warn("answer_trip_reality:", error.message); return null; }
      return data || null;
    } catch (err) {
      console.warn("answer_trip_reality:", err?.message || err);
      return null;
    }
  }, []);

  const updateBudgetGoal = useCallback(async (amount) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!hh || !db) return;

    try {
      const { error: budgetErr } = await db
        .from("households")
        .update({ budget_goal: amount })
        .eq("id", hh.id);
      if (budgetErr) throw budgetErr;
      const updated = { ...hh, budget_goal: amount };
      setHousehold(updated);
      householdRef.current = updated;
    } catch (err) {
      console.error("updateBudgetGoal error:", err.message);
      setError(`Could not update budget: ${err.message}`);
    }
  }, []);

  // ─────────────────────────────────────────────────────────────
  // updatePrice: updates price_per_unit WITHOUT touching quantity.
  // Checks for an existing row first — if found, uses .update()
  // to patch only the price field. If no row exists yet, inserts
  // with quantity 0 as a price-only placeholder.
  // ─────────────────────────────────────────────────────────────
  const updatePrice = useCallback(async (itemName, price) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!hh || !db) return;

    // Optimistic UI update
    setPrices((prev) => ({ ...prev, [itemName]: price }));

    try {
      const catalogItem = catalogRef.current[itemName];
      if (!catalogItem) return;

      // Check if a row already exists for this item
      const { data: existing } = await db
        .from("list_items")
        .select("id, quantity")
        .eq("household_id", hh.id)
        .eq("catalog_item_id", catalogItem.id)
        .is("deleted_at", null)
        .maybeSingle();

      if (existing) {
        // Row exists — update price only, leave quantity untouched
        const { error: priceErr } = await db
          .from("list_items")
          .update({ price_per_unit: price })
          .eq("id", existing.id);
        if (priceErr) throw priceErr;
      } else {
        // No row yet (or soft-deleted) — upsert as price-only placeholder
        const { error: priceErr } = await db
          .from("list_items")
          .upsert(
            {
              household_id: hh.id,
              catalog_item_id: catalogItem.id,
              price_per_unit: price,
              quantity: 0,
              status: "pending",
              added_by: internalUserIdRef.current,
              deleted_at: null,
            },
            { onConflict: "household_id,catalog_item_id" }
          );
        if (priceErr) throw priceErr;
      }
    } catch (err) {
      console.error("updatePrice error:", err.message);
      setError(`Could not update price: ${err.message}`);
    }
  }, []);

  // ── Scoped error ownership ─────────────────────────────────────────────────
  // `error` is ONE state behind ONE toast, shared by every subsystem. That makes a
  // blunt "clear on my success" actively dangerous here: the list poll runs every
  // 2s and the catalog poll every 20s, so an unscoped clear would wipe a user's
  // "Could not create meal" toast within two seconds of it appearing — the toast
  // would become unreadable rather than merely stale.
  //
  // So a message records who set it, and a clear only fires when the clearer still
  // owns what is showing. A poller heals its OWN stale toast — the actual bug, where
  // a background retry succeeds silently and the message has no path to clear — while
  // leaving everyone else's message alone.
  //
  // Setting an error through plain setError is still fine for one-shot user actions:
  // those are read and dismissed by the person who triggered them. Use failWith when
  // the same code path can also SUCCEED later and should tidy up after itself.
  const failWith = useCallback((source, message) => {
    errorSourceRef.current = source;
    setError(message);
  }, []);

  const clearErrorFrom = useCallback((source) => {
    if (errorSourceRef.current !== source) return;   // someone else owns the toast
    errorSourceRef.current = null;
    setError(null);
  }, []);

  const dismissError = useCallback(() => {
    errorSourceRef.current = null;
    setError(null);
  }, []);

  // ─────────────────────────────────────────────────────────────
  // createInvite: generates a 6-char code, writes to
  // household_invites, returns the full invite URL.
  // ─────────────────────────────────────────────────────────────
  const createInvite = useCallback(async () => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!hh || !db) return null;

    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

    try {
      const { error: inviteErr } = await db
        .from("household_invites")
        .insert({
          household_id: hh.id,
          created_by: internalUserIdRef.current,
          code,
          expires_at: expiresAt,
        });
      if (inviteErr) throw inviteErr;
      // DXA event: an invite minted (the depth / second-invite signal). Same
      // tracer pattern as item_added_to_list; household id only — never the
      // code. Telemetry never throws into, or blocks, the invite.
      try {
        tracer.startSpan("household_invite_created", {
          attributes: { household_id: hh.id },
        }).end();
      } catch (e) {
        console.warn("[rum] household_invite_created event failed:", e);
      }
      return `${window.location.origin}?invite=${code}`;
    } catch (err) {
      console.error("createInvite error:", err.message);
      setError(`Could not create invite: ${err.message}`);
      return null;
    }
  }, []);

  // ─────────────────────────────────────────────────────────────
  // acceptInvite: looks up a code, joins the household,
  // reloads app state. Returns household name on success.
  // ─────────────────────────────────────────────────────────────
  const acceptInvite = useCallback(async (code) => {
    const db = supabaseRef.current;
    if (!db || !internalUserIdRef.current) return false;

    try {
      // Migration 030 — the pre-flight lookup, the three JS validations and
      // the client-direct accepted_at stamp all moved INTO the RPC.
      //
      // The lookup could not merely be simplified, it had to go: invites_select
      // is now members-only, so a not-yet-member can no longer resolve a code to
      // a household at all. The RPC's return value replaces it — household_id
      // for the households fetch below, household_name for the success toast.
      //
      // The stamp moved server-side because leaving it here meant an attacker
      // calling the RPC directly and never issuing the UPDATE held a
      // permanently reusable invite. Read, validate, join and stamp are now one
      // transaction.
      //
      // Server raises three distinct messages: "Invite not found." /
      // "This invite has already been used." / "This invite has expired."
      // toUpperCase() is kept as belt-and-braces; the server normalizes with
      // upper(trim(...)) regardless.
      const { data: joinResult, error: joinErr } = await db.rpc("join_household", {
        p_invite_code: code.toUpperCase(),
      });
      if (joinErr) throw new Error(joinErr.message);
      if (!joinResult?.household_id) throw new Error("Invite not found.");

      const { data: hhData } = await db
        .from("households")
        .select("id, name, budget_goal")
        .eq("id", joinResult.household_id)
        .single();

      if (hhData) {
        setHousehold(hhData);
        householdRef.current = hhData;

        // Load catalog (global + household custom items)
        const { data: catalog } = await db
          .from("catalog_items")
          .select("id, name, category, is_staple")
          .eq("is_global", true)
          .is("deleted_at", null);

        const { data: customItems } = await db
          .from("catalog_items")
          .select("id, name, category, is_staple")
          .eq("household_id", hhData.id)
          .eq("is_global", false)
          .is("deleted_at", null);

        const cMap = {};
        [...(catalog || []), ...(customItems || [])].forEach((item) => {
          cMap[item.name] = item;
        });
        setCatalogMap(cMap);
        catalogRef.current = cMap;

        await loadListItems(db, hhData.id);

        // Subscribe to realtime for the new household
        db.channel(`list_items:${hhData.id}`)
          .on(
            "postgres_changes",
            { event: "*", schema: "public", table: "list_items", filter: `household_id=eq.${hhData.id}` },
            () => loadListItems(db, hhData.id)
          )
          .subscribe();
      }

      return joinResult.household_name || hhData?.name || "the place";
    } catch (err) {
      console.error("acceptInvite error:", err.message);
      setError(err.message);
      return false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // removeFromList: list-layer action. Soft-deletes a single list_item from the
  // shared list (catalog row untouched — item remains re-addable). This is the
  // SHOP swipe-to-remove action, distinct from hideItem (catalog, per-user) and
  // deleteItem (catalog, household-wide).
  const removeFromList = useCallback(async (itemName, catalogItemId) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!hh || !db) return;

    const resolvedId = catalogItemId ?? catalogRef.current[itemName]?.id;
    if (!resolvedId) { setError(`"${itemName}" not in catalog`); return; }

    const prevRows = listRows;
    setListRows((rows) => rows.filter(r => r.catalogItemId !== resolvedId));

    try {
      const { error: rmErr } = await db
        .from("list_items")
        .update({ deleted_at: new Date().toISOString() })
        .eq("household_id", hh.id)
        .eq("catalog_item_id", resolvedId)
        .is("deleted_at", null);
      if (rmErr) throw rmErr;
    } catch (err) {
      console.error("removeFromList error:", err.message);
      setError(`Could not remove item: ${err.message}`);
      setListRows(prevRows);
    }
  }, [listRows]);

  // ─────────────────────────────────────────────────────────────
  // hideItem: per-user, view-only. Hides any catalog item (global
  // or custom) from THIS user's browse view. Never affects the
  // shared list or other users. Reversible via Restore.
  // ─────────────────────────────────────────────────────────────
  const hideItem = useCallback(async (itemName) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh) return;

    const catalogItem = catalogRef.current[itemName];
    if (!catalogItem) return;

    // Optimistically remove from this user's browse view
    setQuantities((prev) => { const n = { ...prev }; const rid = liveRowIdFor(catalogItem.id); if (rid) delete n[rid]; delete n[preKey(catalogItem.id)]; return n; });
    setCatalogMap((prev) => { const n = { ...prev }; delete n[itemName]; return n; });
    const newRef = { ...catalogRef.current };
    delete newRef[itemName];
    catalogRef.current = newRef;

    try {
      const { error: hideErr } = await db
        .from("user_hidden_items")
        .insert({
          clerk_id: clerkIdRef.current,
          catalog_item_id: catalogItem.id,
        });
      if (hideErr && !hideErr.message.includes("duplicate")) throw hideErr;

      hiddenIdsRef.current = new Set([...hiddenIdsRef.current, catalogItem.id]);
      hiddenCatalogItemsRef.current = [...hiddenCatalogItemsRef.current, catalogItem];
      setHiddenCatalogItems(prev => [...prev, catalogItem]);
    } catch (err) {
      console.error("hideItem error:", err.message);
      setError(`Could not hide item: ${err.message}`);
      // Rollback
      catalogRef.current = { ...catalogRef.current, [itemName]: catalogItem };
      setCatalogMap((prev) => ({ ...prev, [itemName]: catalogItem }));
    }
  }, []);

  // ─────────────────────────────────────────────────────────────
  // deleteItem: HARD-deletes a CUSTOM (household-owned) catalog item
  // for the entire household via the delete_custom_catalog_item RPC.
  // The RPC removes the catalog row plus all references (list_items,
  // list_item_contributors, user_hidden_items, waste_events).
  // Global/seed items are NOT deletable — use hideItem for those.
  // ─────────────────────────────────────────────────────────────
  const deleteItem = useCallback(async (itemName) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh) return;
    const catalogItem = catalogRef.current[itemName];
    if (!catalogItem) return;
    // Guard: deletion is custom-only. Seed/global items can only be hidden.
    if (catalogItem.is_global) {
      console.warn("deleteItem called on a global item — ignored. Use hideItem.");
      return;
    }

    // Snapshot for rollback, then optimistically remove from UI
    const prevCatalogRef = catalogRef.current;
    setQuantities((prev) => { const n = { ...prev }; const rid = liveRowIdFor(catalogItem.id); if (rid) delete n[rid]; delete n[preKey(catalogItem.id)]; return n; });
    setCatalogMap((prev) => { const n = { ...prev }; delete n[itemName]; return n; });
    const newRef = { ...catalogRef.current };
    delete newRef[itemName];
    catalogRef.current = newRef;

    // Guard the poll from re-adding this item during the RPC round-trip
    deletedIdsRef.current = new Set([...deletedIdsRef.current, catalogItem.id]);

    try {
      const { error: rpcErr } = await db.rpc("delete_custom_catalog_item", {
        p_household_id: hh.id,
        p_catalog_item_id: catalogItem.id,
      });
      if (rpcErr) throw rpcErr;
    } catch (err) {
      console.error("deleteItem error:", err.message);
      setError(`Could not delete item: ${err.message}`);
      // Rollback — un-mark deleted and restore local catalog state
      deletedIdsRef.current = new Set([...deletedIdsRef.current].filter(id => id !== catalogItem.id));
      catalogRef.current = prevCatalogRef;
      setCatalogMap((prev) => ({ ...prev, [itemName]: catalogItem }));
    }
  }, []);

  const restoreHiddenByCategory = useCallback(async (rawCategoryName) => {
    const db = supabaseRef.current;
    if (!db) return;

    const toRestore = hiddenCatalogItemsRef.current.filter(item => (item.category || "") === rawCategoryName);
    if (toRestore.length === 0) return;

    const ids = toRestore.map(item => item.id);
    const { error: restoreErr } = await db
      .from("user_hidden_items")
      .delete()
      .eq("clerk_id", clerkIdRef.current)
      .in("catalog_item_id", ids);
    if (restoreErr) { setError(`Could not restore items: ${restoreErr.message}`); return; }

    // Remove from hiddenIdsRef
    const newHiddenIds = new Set(hiddenIdsRef.current);
    ids.forEach(id => newHiddenIds.delete(id));
    hiddenIdsRef.current = newHiddenIds;

    // Add restored items back to catalog
    const restoredMap = {};
    toRestore.forEach(item => { restoredMap[item.name] = item; });
    catalogRef.current = { ...catalogRef.current, ...restoredMap };
    setCatalogMap(prev => ({ ...prev, ...restoredMap }));

    // Remove from hiddenCatalogItems
    hiddenCatalogItemsRef.current = hiddenCatalogItemsRef.current.filter(item => !ids.includes(item.id));
    setHiddenCatalogItems(prev => prev.filter(item => !ids.includes(item.id)));
  }, []);

  // Un-hide a SINGLE catalog item (F1b). The un-hide-only primitive: it deletes the
  // user_hidden_items row, clears hiddenIdsRef, and restores the item to catalogRef.
  // It NEVER touches list_items — revealing a shared row leaves its quantity exactly
  // as the household set it. Single-item twin of restoreHiddenByCategory.
  const unhideItem = useCallback(async (catalogItemId) => {
    const db = supabaseRef.current;
    if (!db) return;

    const item = hiddenCatalogItemsRef.current.find(it => it.id === catalogItemId);
    if (!item) return;

    const { error: restoreErr } = await db
      .from("user_hidden_items")
      .delete()
      .eq("clerk_id", clerkIdRef.current)
      .eq("catalog_item_id", catalogItemId);
    if (restoreErr) { setError(`Could not reveal item: ${restoreErr.message}`); return; }

    const newHiddenIds = new Set(hiddenIdsRef.current);
    newHiddenIds.delete(catalogItemId);
    hiddenIdsRef.current = newHiddenIds;

    catalogRef.current = { ...catalogRef.current, [item.name]: item };
    setCatalogMap(prev => ({ ...prev, [item.name]: item }));

    hiddenCatalogItemsRef.current = hiddenCatalogItemsRef.current.filter(it => it.id !== catalogItemId);
    setHiddenCatalogItems(prev => prev.filter(it => it.id !== catalogItemId));
  }, []);

  const refreshCatalog = useCallback(async () => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh) return;

    // ── "JWT not yet valid" — bounded single retry ────────────────────────────
    // Clock skew between GoTrue's token `iat` and PostgREST's validation clock:
    // the token is genuinely fine, it is just a second or so early. It clears on
    // its own in ~1-2s, which is well inside one 20s poll tick.
    //
    // This is NOT only a cold-start problem. Clerk rotates the session token
    // periodically, so the skew window reopens MID-SESSION — which is exactly why
    // it surfaces on this poll rather than only at sign-in, and why the boot
    // effect's backoff (above) does not already cover it.
    //
    // The boot-time global-catalog read already retries-with-backoff for the same
    // underlying class ("session/RLS not yet warm"). This keeps the poll consistent
    // with that established pattern instead of solving one symptom two ways in one
    // file.
    //
    // ⚠️ SCOPED TO THIS ONE MESSAGE, DELIBERATELY. Do not widen it to every phrase
    // in `transientPhrases`: those are transport failures, and retrying a
    // genuinely-down connection on a 20s tick just hammers it. This phrase is the
    // one case where the server answered and the answer means "ask again shortly".
    //
    // One retry only. If it fails twice the skew is not the ordinary kind, and the
    // existing classifyFetchError path owns it from there — now reporting
    // 'transient', so it surfaces as the quiet pill rather than a raw JWT toast.
    const withJwtRetry = async (runQuery) => {
      let result = await runQuery();
      const msg = result.error?.message;
      if (typeof msg === "string" && msg.includes("JWT not yet valid")) {
        await new Promise((r) => setTimeout(r, 1500));
        if (!supabaseRef.current) return result;  // torn down mid-wait — don't re-issue
        result = await runQuery();
      }
      return result;
    };

    const { data: catalog, error: catalogErr, status: catalogStatus } = await withJwtRetry(() => db
      .from("catalog_items")
      .select("id, name, category, unit, is_global, price_hint, is_staple")
      .eq("is_global", true)
      .is("deleted_at", null));
    if (catalogErr) {
      if (stopOnAuthFailure(catalogErr, catalogStatus)) return;
      if (classifyFetchError(catalogErr) === 'transient') { reportTransientFailure(); } else { failWith("catalog", `Could not refresh catalog: ${catalogErr.message}`); }
      return;
    }

    // Same retry on the custom-items read. It runs after the global one, so in the
    // ordinary skew case the wait above has already cleared the window and this
    // never fires — but a rotation landing between the two queries would otherwise
    // fail the poll on the second half, which looks identical to the user.
    const { data: customItems, error: customErr, status: customStatus } = await withJwtRetry(() => db
      .from("catalog_items")
      .select("id, name, category, unit, is_global, price_hint, is_staple")
      .eq("household_id", hh.id)
      .eq("is_global", false)
      .is("deleted_at", null));
    if (customErr) {
      if (stopOnAuthFailure(customErr, customStatus)) return;
      if (classifyFetchError(customErr) === 'transient') { reportTransientFailure(); } else { failWith("catalog", `Could not refresh catalog: ${customErr.message}`); }
      return;
    }

    reportSuccess();
    // Both catalog reads came back clean. THIS is the fix for the observed bug:
    // the poll set "Could not refresh catalog: JWT expired", then quietly started
    // succeeding again, and the toast had no path to clear itself — it sat there
    // looking like a live failure while 200s streamed past in the Network tab.
    clearErrorFrom("catalog");

    // Per-household staples (row-presence, migration 016). Reading this here —
    // instead of the shared catalog_items.is_staple column — is what makes a
    // global-item staple PERSIST across the poll instead of reverting to grey.
    const stapleSet = await fetchStapleSet(db, hh.id);

    const next = {};
    (catalog || []).forEach((item) => {
      if (hiddenIdsRef.current.has(item.id) || deletedIdsRef.current.has(item.id)) return; // never show hidden or just-deleted
      next[item.name] = { ...item, is_staple: stapleSet.has(item.id), created_by: null };
    });
    (customItems || []).forEach((item) => {
      if (hiddenIdsRef.current.has(item.id) || deletedIdsRef.current.has(item.id)) return;
      next[item.name] = { ...item, is_staple: stapleSet.has(item.id), created_by: item.created_by ?? "custom" };
    });

    // Guarded merge: only commit if the catalog actually changed, so a
    // 20s poll doesn't clobber optimistic local edits or cause flicker.
    const prev = catalogRef.current;
    const prevKeys = Object.keys(prev);
    const nextKeys = Object.keys(next);
    let changed = prevKeys.length !== nextKeys.length;
    if (!changed) {
      for (const k of nextKeys) {
        const a = prev[k], b = next[k];
        if (!a || a.id !== b.id || a.category !== b.category || a.is_staple !== b.is_staple || a.price_hint !== b.price_hint) {
          changed = true;
          break;
        }
      }
    }
    if (changed) {
      catalogRef.current = next;
      setCatalogMap(next);
    }
  }, [reportTransientFailure, reportSuccess, failWith, clearErrorFrom]);

  // Keep the ref pointing at the latest refreshCatalog so the boot-effect
  // catalog poll can call it without taking it as an effect dependency.
  refreshCatalogRef.current = refreshCatalog;

  const refreshMembers = useCallback(async () => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh) return;
    try {
      const { data: members } = await db
        .from("household_members")
        .select("id, user_id, role")
        .eq("household_id", hh.id)
        .is("deleted_at", null);
      const { data: profiles } = await db
        .rpc("get_household_member_profiles", { p_household_id: hh.id });
      const membersWithProfiles = (members || []).map(m => ({
        ...m,
        users: profiles?.find(p => p.user_id === m.user_id) || null
      }));
      setHouseholdMembers(membersWithProfiles);
      householdMembersRef.current = membersWithProfiles;
      setMembersLoadedFor(hh.id);
    } catch (err) {
      console.error("refreshMembers error:", err.message);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // refs-only, intentional empty deps

  const renameItem = useCallback(async (oldName, newName) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh) return;
    const item = catalogRef.current[oldName];
    if (!item || item.created_by == null) return; // only custom items
    const { error: renameErr } = await db
      .from("catalog_items")
      .update({ name: newName })
      .eq("id", item.id)
      .eq("household_id", hh.id);
    if (renameErr) { setError(`Could not rename item: ${renameErr.message}`); return; }
    const updated = { ...item, name: newName };
    const newRef = { ...catalogRef.current };
    delete newRef[oldName];
    newRef[newName] = updated;
    catalogRef.current = newRef;
    setCatalogMap(prev => {
      const next = { ...prev };
      delete next[oldName];
      next[newName] = updated;
      return next;
    });
  }, []);

  const toggleStaple = useCallback(async (itemName) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh) return;
    const item = catalogRef.current[itemName];
    if (!item) return;
    const newVal = !item.is_staple;
    // Staple is per-household row-presence in household_staples (migration 016),
    // uniform for global AND custom items. The old catalog_items UPDATE silently
    // no-op'd on global rows (household_id=NULL failed the RLS USING clause).
    if (newVal) {
      const { error: insErr } = await db
        .from("household_staples")
        .insert({ household_id: hh.id, catalog_item_id: item.id });
      // 23505 = unique_violation (already stapled: double-tap / race) → success.
      if (insErr && insErr.code !== "23505") { setError(`Could not update staple: ${insErr.message}`); return; }
    } else {
      const { error: delErr } = await db
        .from("household_staples")
        .delete()
        .eq("household_id", hh.id)
        .eq("catalog_item_id", item.id);
      if (delErr) { setError(`Could not update staple: ${delErr.message}`); return; }
    }
    const updated = { ...item, is_staple: newVal };
    catalogRef.current = { ...catalogRef.current, [itemName]: updated };
    setCatalogMap(prev => ({ ...prev, [itemName]: updated }));
  }, []);

  const updateFullName = useCallback(async (newName, onClerkUpdate) => {
    const db = supabaseRef.current;
    if (!db || !internalUserIdRef.current) return false;
    const trimmed = newName.trim();
    if (!trimmed) return false;
    try {
      const { error: nameErr } = await db
        .from("users")
        .update({ full_name: trimmed })
        .eq("id", internalUserIdRef.current);
      if (nameErr) throw nameErr;
      if (onClerkUpdate) await onClerkUpdate(trimmed);
      return true;
    } catch (err) {
      console.error("updateFullName error:", err.message);
      setError(`Could not update name: ${err.message}`);
      return false;
    }
  }, []);

  const createHousehold = useCallback(async (name) => {
    const db = supabaseRef.current;
    if (!db || !clerkIdRef.current) return null;
    try {
      const { data, error: createErr } = await db
        .rpc("create_household", { p_name: name, p_clerk_id: clerkIdRef.current });
      if (createErr) throw createErr;
      return data?.household_id || null;
    } catch (err) {
      console.error("createHousehold error:", err.message);
      setError(`Could not create place: ${err.message}`);
      return null;
    }
  }, []);

  // ─────────────────────────────────────────────────────────────
  // OurBanner — photo upload + framing/name commit (migration 024)
  //
  // uploadHouseholdPhoto: EXIF-normalize + downscale, upload to
  //   {household_id}/header.jpg (upsert = replace overwrites), then
  //   return the stored object path. Does NOT persist the row — the
  //   caller commits path + framing + name together via
  //   updateHouseholdBanner (one Save, per spec D8).
  // ─────────────────────────────────────────────────────────────
  const uploadHouseholdPhoto = useCallback(async (file) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh || !file) {
      console.error("[photo upload] aborted before start:", { hasDb: !!db, hasHousehold: !!hh, hasFile: !!file });
      return null;
    }
    // Stage 1 — EXIF-normalize + downscale (client-side canvas work). This is the
    // step most likely to throw on browser-support gaps, so it's isolated with its
    // own label; a throw here means the .upload() below is never reached (no
    // network request), which is exactly the silent-no-op signature.
    let blob;
    try {
      blob = await normalizeHouseholdPhoto(file);
    } catch (err) {
      console.error("[photo upload] normalize/EXIF step threw:", err);
      setError(`Could not process photo: ${err?.message || err}`);
      return null;
    }
    // Stage 2 — storage upload (the network request to household-photos).
    try {
      const path = `${hh.id}/header.jpg`;
      const { error: upErr } = await db.storage
        .from("household-photos")
        .upload(path, blob, { contentType: "image/jpeg", upsert: true });
      if (upErr) throw upErr;
      return path;
    } catch (err) {
      console.error("[photo upload] storage .upload() step threw:", err);
      setError(`Could not upload photo: ${err?.message || err}`);
      return null;
    }
  }, []);

  // updateHouseholdBanner: commit name + framing + wordmark (+ optional
  // photo_path change) in one update. Pass only the fields to change;
  // `photo_path: null` removes the photo. Re-resolves the signed URL and
  // updates household state so the header swaps without a reload.
  const updateHouseholdBanner = useCallback(async (patch) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh) return false;
    // Only send known banner/name columns.
    const allowed = ["name", "photo_path", "photo_position_x", "photo_position_y", "photo_zoom", "banner_wordmark"];
    const update = {};
    for (const k of allowed) if (k in patch) update[k] = patch[k];
    if (Object.keys(update).length === 0) return true;
    if (typeof update.name === "string") {
      const trimmed = update.name.trim();
      if (!trimmed) { delete update.name; } else { update.name = trimmed; }
    }
    try {
      const { error: updErr } = await db
        .from("households").update(update).eq("id", hh.id);
      if (updErr) throw updErr;
      const updated = { ...hh, ...update };
      // Re-resolve the signed URL when the photo path changed.
      if ("photo_path" in update) {
        if (update.photo_path) {
          try {
            const { data: signed } = await db.storage
              .from("household-photos")
              .createSignedUrl(update.photo_path, 60 * 60);
            updated.photoUrl = signed?.signedUrl || null;
          } catch (_e) { updated.photoUrl = null; }
        } else {
          updated.photoUrl = null;
        }
      }
      setHousehold(updated);
      householdRef.current = updated;
      return true;
    } catch (err) {
      console.error("updateHouseholdBanner error:", err.message);
      setError(`Could not save changes: ${err.message}`);
      return false;
    }
  }, []);

  // removeHouseholdPhoto: delete the stored object AND null out the row's
  // photo_path/framing. banner_wordmark is intentionally NOT reset — per spec
  // "Dormancy": the household's wordmark choice stays dormant and reapplies if
  // a photo is added again. Resetting it would silently discard their choice.
  const removeHouseholdPhoto = useCallback(async () => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh) return false;
    try {
      if (hh.photo_path) {
        // Best-effort object delete; the row update below is the source of truth.
        await db.storage.from("household-photos").remove([hh.photo_path]);
      }
      return await updateHouseholdBanner({
        photo_path: null, photo_position_x: 50, photo_position_y: 50, photo_zoom: 100,
      });
    } catch (err) {
      console.error("removeHouseholdPhoto error:", err.message);
      setError(`Could not remove photo: ${err.message}`);
      return false;
    }
  }, [updateHouseholdBanner]);

  // ─────────────────────────────────────────────────────────────
  // Meal photos v1 (065; SPEC_meal_photos_v1 + the 2026-10-13 / 10-20 amendments)
  //
  // addMealPhoto (= uploadMealPhoto, then saveMealPhoto): two canvas renditions (thumb ~480, view ~1080; EXIF dropped by
  //   the re-encode), BOTH objects uploaded to the private meal-photos bucket
  //   first (new uuid stem, upsert false, cacheControl 3600 — R7/R9), THEN the
  //   add_meal_photo RPC, which checks the paths and that both objects exist.
  //   Slice 1 never attaches a cook (R3): p_meal_cook_id is null. Returns
  //   { ok: true, id } or { ok: false, reason: "decode" | "upload" | "save" } —
  //   the caller says it quietly (a toast, never the error banner). An upload
  //   whose RPC fails leaves orphan objects for the later service-role sweep.
  // removeMealPhoto: the remove_meal_photo RPC (soft delete; any member, P3).
  // fetchMealPhotos: the household's LIVE photo rows, newest first
  //   (uploaded_at desc, id desc — the RPC's own cap order). null on failure, so
  //   the caller keeps what it had rather than blanking the cards.
  // ─────────────────────────────────────────────────────────────
  // SPLIT (SPEC_photo_menu DAN-1, 2026-10-25): uploadMealPhoto renders and stores the two
  // objects and returns { ok: true, upload } — nothing is on the meal yet, so a failure here
  // changes nothing the household sees; saveMealPhoto(upload, cookId) is the add_meal_photo RPC.
  // Replace runs its own steps between the two; addMealPhoto is still upload then save.
  const uploadMealPhoto = useCallback(async (mealId, file) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh || !mealId || !file) return { ok: false, reason: "save" };
    let r;
    try {
      r = await renderMealPhoto(file);
    } catch (err) {
      console.warn("[meal photo] could not decode:", file && file.type, err && (err.message || err));
      return { ok: false, reason: "decode" };
    }
    const stem = crypto.randomUUID();
    const { thumbPath, viewPath } = mealPhotoPaths(hh.id, mealId, stem, r.ext);
    const bucket = db.storage.from("meal-photos");
    const opts = { contentType: r.type, upsert: false, cacheControl: "3600" };
    try {
      const [t, v] = await Promise.all([bucket.upload(thumbPath, r.thumb.blob, opts), bucket.upload(viewPath, r.view.blob, opts)]);
      if (t.error) throw t.error;
      if (v.error) throw v.error;
    } catch (err) {
      console.warn("[meal photo] upload failed:", err && (err.message || err));
      return { ok: false, reason: "upload" };
    }
    return { ok: true, upload: { mealId, thumbPath, viewPath, width: r.view.width, height: r.view.height } };
  }, []);

  // cookId (slice 2, D1/S4): the afterglow's cook — the photo is cook-attached and P1's per-cook
  // replace applies. Omitted (the recipe view) → null, R3.
  const saveMealPhoto = useCallback(async (upload, cookId = null) => {
    const db = supabaseRef.current;
    if (!db || !upload) return { ok: false, reason: "save" };
    const { data: id, error: err } = await db.rpc("add_meal_photo", {
      p_meal_id: upload.mealId, p_meal_cook_id: cookId || null, p_thumb_path: upload.thumbPath, p_view_path: upload.viewPath,
      p_width: upload.width, p_height: upload.height,
    });
    if (err) {
      console.warn("[meal photo] add_meal_photo failed:", err.code, err.message);
      return { ok: false, reason: "save" };
    }
    return { ok: true, id };
  }, []);

  const addMealPhoto = useCallback(async (mealId, file, cookId = null) => {
    const up = await uploadMealPhoto(mealId, file);
    if (!up.ok) return up;
    return saveMealPhoto(up.upload, cookId);
  }, [uploadMealPhoto, saveMealPhoto]);

  // THE FLIP (073 set_photo_kind; SPEC_meal_sharing_v2 S46, Dan 2026-10-30): a photo is born a
  // meal photo (kind = cook, add_meal_photo above) and its AUTHOR flips it — to showcase (the one
  // photo that can travel on their own public gift) or back to a meal photo. The server checks
  // the author (uploaded_by = the JWT's user; 42501 for anyone else), retires the author's other
  // live showcase on the meal when flipping to showcase, and REFUSES a flip to cook at 3 live cook
  // photos (22023 "meal photos are full") rather than age one out. meal_cook_id is left as-is.
  // Returns { ok: true } or { ok: false, reason, line } — the line is the RPC's message or a plain
  // mapping of its SQLSTATE (src/lib/mealPhotos.js setKindFailure); the caller shows it, never swallows it.
  // (072's add_showcase_photo stays in the database, unused by the client — S46.)
  const setPhotoKind = useCallback(async (photoId, kind) => {
    const db = supabaseRef.current;
    if (!db || !photoId || (kind !== "cook" && kind !== "showcase")) return { ok: false, ...setKindFailure(null) };
    const { error: err } = await db.rpc("set_photo_kind", { p_photo_id: photoId, p_kind: kind });
    if (err) {
      console.warn("[photo kind] set_photo_kind failed:", err.code, err.message);
      return { ok: false, ...setKindFailure(err) };
    }
    return { ok: true };
  }, []);

  // ─────────────────────────────────────────────────────────────
  // GIVE THIS MEAL (072 §5 give_meal / revoke_meal_gifts; SPEC_meal_sharing_phase_a_client Commit 2;
  // S34 / S41 / S44). The server builds the snapshot and picks the photo (S12: the giver's own latest
  // showcase, or none) and mints the 16-char code. The CLIENT then copies the two renditions into
  // gift-photos/{gift_id}/ — view first, then thumb, no upsert (C4) — before the share sheet ever
  // opens (S34), then asks the server to mark the gift (attach_gift_photo, 074 / S47). Bytes travel
  // in the format they were stored in (webp or jpeg), never re-encoded. The code is returned to the
  // caller for the share payload only; it is never written to state the DOM could carry.
  // ─────────────────────────────────────────────────────────────
  const giveMeal = useCallback(async (mealId, note) => {
    const db = supabaseRef.current;
    if (!db || !mealId) return { ok: false, ...giveFailure(null) };
    const { data, error: err } = await db.rpc("give_meal", { p_meal_id: mealId, p_note: note == null ? null : note });
    if (err) { console.warn("[give] give_meal failed:", err.code, err.message); return { ok: false, ...giveFailure(err) }; }
    const d = data || {};
    return { ok: true, code: d.code, giftId: d.gift_id, photoPath: d.photo_path || null, thumbPath: d.photo_thumb_path || null };
  }, []);

  // The photo copy (C4 / S47). Signs the two source paths in meal-photos (the giver is a member),
  // fetches the bytes, uploads them under the gift id as {gift_id}/(view|thumb).{ext} with the
  // source blob's own contentType — "webp" or "jpg" from the bytes' type, the two formats the bucket
  // admits (074); both renditions must share it — then attach_gift_photo(gift_id, ext): the SERVER
  // checks both objects exist in that one format and marks meal_gifts.photo_ext. Any failure — a
  // refused upload, a missing object, an unexpected type, a mismatched pair, a refused mark — is one
  // answer: { ok: false } (B5); no retry, no delete (the S37 sweep collects strays).
  const copyGiftPhoto = useCallback(async (giftId, viewPath, thumbPath) => {
    const db = supabaseRef.current;
    if (!db || !giftId || !viewPath || !thumbPath) return { ok: false };
    try {
      const { data: signed, error: sErr } = await db.storage.from("meal-photos").createSignedUrls([viewPath, thumbPath], 60);
      if (sErr) throw sErr;
      const urlFor = (path) => { const d = (signed || []).find((x) => x && x.path === path && !x.error); return d ? d.signedUrl : null; };
      const sources = { view: viewPath, thumb: thumbPath };
      const gift = db.storage.from("gift-photos");
      let ext = null;
      for (const rendition of GIFT_RENDITIONS) {
        const url = urlFor(sources[rendition]);
        if (!url) throw new Error(`no signed url for the ${rendition} rendition`);
        const res = await fetch(url);
        if (!res.ok) throw new Error(`${rendition} fetch ${res.status}`);
        const blob = await res.blob();
        const e = giftExt(blob.type);
        if (!e) throw new Error(`${rendition} rendition is ${blob.type || "untyped"}, not webp or jpeg`);
        if (ext && e !== ext) throw new Error(`${rendition} rendition is ${e}, view was ${ext}`);
        ext = e;
        const { error: upErr } = await gift.upload(giftObjectPath(giftId, rendition, ext), blob, { contentType: blob.type, upsert: false, cacheControl: "3600" });
        if (upErr) throw upErr;
      }
      const { error: aErr } = await db.rpc("attach_gift_photo", { p_gift_id: giftId, p_ext: ext });
      if (aErr) throw aErr;
      return { ok: true };
    } catch (e) {
      console.warn("[give] gift photo copy failed (B5):", e && (e.message || e));
      return { ok: false };
    }
  }, []);

  // Stop giving (10-07 V11): revokes every live gift of the meal. Returns the revoked count only.
  const revokeMealGifts = useCallback(async (mealId) => {
    const db = supabaseRef.current;
    if (!db || !mealId) return { ok: false };
    const { data, error: err } = await db.rpc("revoke_meal_gifts", { p_meal_id: mealId });
    if (err) { console.warn("[give] revoke_meal_gifts failed:", err.code, err.message); return { ok: false }; }
    return { ok: true, count: Number(data) || 0 };
  }, []);

  // The meal's LIVE gifts, for "Stop giving" (shown while any exists). RLS: members of the giving
  // household. Only ids and dates — the code is a bearer key and never comes back into the client.
  const fetchMealGifts = useCallback(async (mealId) => {
    const db = supabaseRef.current;
    if (!db || !mealId) return null;
    const { data, error: err } = await db.from("meal_gifts").select("id, created_at").eq("source_meal_id", mealId).is("revoked_at", null);
    if (err) { console.warn("[give] meal_gifts read failed (non-fatal):", err.message); return null; }
    return data || [];
  }, []);

  // ─────────────────────────────────────────────────────────────
  // THE SIGNED-IN ARRIVAL (072 preview_meal_gift / record_meal_gift_receipt / accept_gift_photo;
  // SPEC_meal_sharing_phase_a_client Commit 3; S24, S25, S36, S40). preview reads the gift for THIS
  // place (membership first; not_found P0002, revoked 55000). The receipt is written after the
  // person's normal save, idempotent, retried once silently (the 09-08 residual: a meal without a
  // receipt is still theirs). The photo copy downloads the gift's two renditions in the gift's own
  // format (photo_ext; the SELECT policy admits authenticated while the gift is live and marked),
  // uploads them byte for byte under the RECEIVER's own folder with the same extension (S47),
  // then accept_gift_photo records a showcase row whose uploaded_by is the GIVER (derived, never
  // passed — S23 / S36). Photo failure is quiet beyond one toast; the meal is already theirs.
  // ─────────────────────────────────────────────────────────────
  const previewMealGift = useCallback(async (code) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh || !code) return { ok: false, ...arrivalFailure(null) };
    const { data, error: err } = await db.rpc("preview_meal_gift", { p_code: code, p_household_id: hh.id });
    if (err) { console.warn("[gift] preview_meal_gift failed:", err.code, err.message); return { ok: false, ...arrivalFailure(err) }; }
    const d = data || {};
    // 074 / S47: photo_ext ("webp" | "jpg" | null) is the server's mark and the name to sign; has_photo
    // on the server is `photo_ext is not null`, so the client derives the flag from the same field.
    const photoExt = d.photo_ext === "webp" || d.photo_ext === "jpg" ? d.photo_ext : null;
    // C10 (Dan 2026-10-30): the arrival form shows the gift photo at the top, read-only — the view
    // rendition signed from gift-photos/{gift_id}/view.{photo_ext}, as the card signs it (S35: readable
    // while the gift is live and marked). A signing failure is no photo in the form; the copy on Add
    // (acceptGiftPhoto) signs again for itself. The URL lives in the sheet's props only, never in state.
    let photoUrl = null;
    if (photoExt && d.gift_id) {
      const { data: signed, error: sErr } = await db.storage.from("gift-photos").createSignedUrl(giftSourcePaths(d.gift_id, photoExt).view, 60 * 60);
      if (sErr) console.warn("[gift] gift photo sign failed (non-fatal):", sErr.message);
      else photoUrl = (signed && signed.signedUrl) || null;
    }
    // 075 / C11: is_giving_place = this place is the gift's own giving household (the server compares;
    // the household id never comes back). source_meal_id = that place's live source meal, only then.
    const isGivingPlace = d.is_giving_place === true;
    const sourceMealId = isGivingPlace && typeof d.source_meal_id === "string" ? d.source_meal_id : null;
    return { ok: true, giftId: d.gift_id, snapshot: d.snapshot || {}, note: d.note || null, alreadyAdded: d.already_added || null, hasPhoto: photoExt !== null, photoExt, photoUrl, isGivingPlace, sourceMealId };
  }, []);

  const recordMealGiftReceipt = useCallback(async (code, mealId) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh || !code || !mealId) return { ok: false };
    for (let attempt = 0; attempt < 2; attempt += 1) {   // one silent retry (10-07 residual)
      const { error: err } = await db.rpc("record_meal_gift_receipt", { p_code: code, p_household_id: hh.id, p_meal_id: mealId });
      if (!err) return { ok: true };
      console.warn(`[gift] record_meal_gift_receipt failed (${attempt + 1}/2):`, err.code, err.message);
      if (err.code === "P0002" || err.code === "55000" || err.code === "42501") return { ok: false, code: err.code };   // the server said no; a retry cannot change it
    }
    return { ok: false };
  }, []);

  // Dimensions of the view rendition for the row (null in the row when unreadable — the RPC stores
  // only positive values).
  const measureBlob = async (blob) => {
    try {
      if (typeof createImageBitmap !== "function") return { width: 0, height: 0 };
      const bmp = await createImageBitmap(blob);
      const out = { width: bmp.width || 0, height: bmp.height || 0 };
      if (bmp.close) bmp.close();
      return out;
    } catch (e) { return { width: 0, height: 0 }; }
  };

  const acceptGiftPhoto = useCallback(async (giftId, mealId, photoExt) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh || !giftId || !mealId || !(photoExt === "webp" || photoExt === "jpg")) return { ok: false };
    try {
      // S47: the gift's format (preview_meal_gift.photo_ext) names the source objects and the copies.
      const src = giftSourcePaths(giftId, photoExt);
      const { data: signed, error: sErr } = await db.storage.from("gift-photos").createSignedUrls([src.view, src.thumb], 60);
      if (sErr) throw sErr;
      const urlFor = (path) => { const d = (signed || []).find((x) => x && x.path === path && !x.error); return d ? d.signedUrl : null; };
      const get = async (path) => {
        const url = urlFor(path);
        if (!url) throw new Error(`no signed url for ${path}`);
        const res = await fetch(url);
        if (!res.ok) throw new Error(`fetch ${path} ${res.status}`);
        return res.blob();
      };
      const viewBlob = await get(src.view);
      const thumbBlob = await get(src.thumb);
      const { width, height } = await measureBlob(viewBlob);
      const { thumbPath, viewPath } = acceptCopyPaths(hh.id, mealId, crypto.randomUUID(), photoExt);
      const bucket = db.storage.from("meal-photos");
      const mime = photoExt === "jpg" ? "image/jpeg" : "image/webp";
      const opts = (b) => ({ contentType: b.type || mime, upsert: false, cacheControl: "3600" });
      const [t, v] = await Promise.all([bucket.upload(thumbPath, thumbBlob, opts(thumbBlob)), bucket.upload(viewPath, viewBlob, opts(viewBlob))]);
      if (t.error) throw t.error;
      if (v.error) throw v.error;
      // thumb first, then view — 065's order, the 10-29 ruling.
      const { error: rErr } = await db.rpc("accept_gift_photo", {
        p_gift_id: giftId, p_household_id: hh.id, p_thumb_path: thumbPath, p_view_path: viewPath, p_width: width, p_height: height,
      });
      if (rErr) throw rErr;
      return { ok: true };
    } catch (e) {
      console.warn("[gift] accept photo failed (quiet):", e && (e.code || ""), e && (e.message || e));
      return { ok: false };
    }
  }, []);

  const removeMealPhoto = useCallback(async (photoId) => {
    const db = supabaseRef.current;
    if (!db || !photoId) return false;
    const { error: err } = await db.rpc("remove_meal_photo", { p_photo_id: photoId });
    if (err) { console.warn("[meal photo] remove_meal_photo failed:", err.code, err.message); return false; }
    return true;
  }, []);

  const fetchMealPhotos = useCallback(async () => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh) return null;
    const { data, error: err } = await db
      .from("meal_photos")
      // meal_cooks(cooked_at) through meal_cook_id (S5): a cook-attached photo's caption reads
      // "Cooked {date}". Null for recipe-view photos (R3). RLS: meal_cooks SELECT is membership.
      // kind (072): 'cook' or 'showcase' — the client's cap and the S28 pick read it.
      // showcased_by / showcased_at / received_at (076, S48): Photo details' "Made showcase by", the
      // give sheet's B1 line, and the received mark (no "Make showcase"; never the meal's showcase).
      .select("id, meal_id, meal_cook_id, kind, thumb_path, view_path, uploaded_by, uploaded_at, width, height, showcased_by, showcased_at, received_at, meal_cooks(cooked_at)")
      .eq("household_id", hh.id)
      .is("deleted_at", null)
      .order("uploaded_at", { ascending: false })
      .order("id", { ascending: false });
    if (err) { console.warn("[meal photo] read failed (non-fatal):", err.message); return null; }
    return (data || []).map(({ meal_cooks: mc, ...r }) => ({ ...r, cooked_at: mc ? mc.cooked_at : null }));
  }, []);

  // ─────────────────────────────────────────────────────────────
  // Cook ratings (066; slice 2, S1/S2) — per person, per cook.
  // rateCook: the rate_cook RPC writes ONLY the caller's own row (the caller is the JWT, there
  //   is no user parameter); rating and note both empty → the row is deleted (no vote is not a
  //   rating). Returns true / false; the caller says a failure quietly.
  // fetchCookRatings: every member's rows for the given cooks (RLS: membership). null on failure.
  // ─────────────────────────────────────────────────────────────
  const rateCook = useCallback(async (cookId, rating, note) => {
    const db = supabaseRef.current;
    if (!db || !cookId) return false;
    const { error: err } = await db.rpc("rate_cook", { p_cook_id: cookId, p_rating: rating == null ? null : rating, p_note: note == null ? null : note });
    if (err) { console.warn("[rating] rate_cook failed:", err.code, err.message); return false; }
    return true;
  }, []);

  const fetchCookRatings = useCallback(async (cookIds) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    const ids = (cookIds || []).filter(Boolean);
    if (!db || !hh) return null;
    if (ids.length === 0) return [];
    const { data, error: err } = await db
      .from("meal_cook_ratings")
      .select("meal_cook_id, user_id, rating, note, updated_at")
      .eq("household_id", hh.id)
      .in("meal_cook_id", ids);
    if (err) { console.warn("[rating] read failed (non-fatal):", err.message); return null; }
    return data || [];
  }, []);

  // ─────────────────────────────────────────────────────────────
  // Solo-start experience (SPEC_solo_start_experience.md)
  // ─────────────────────────────────────────────────────────────

  // joinHouseholdByCode: the welcome sheet's code entrance (D5).
  //
  // ONE JOIN IMPLEMENTATION, TWO ENTRANCES. This is deliberately NOT a second join
  // path — it calls the same server-validated join_household(text) (migration 030) that
  // the ?invite= arrival calls, and hands off to the same durable-intent flags the
  // bootstrap invite branch writes. The sheet is a new door onto the existing room.
  //
  // Returns a result object rather than throwing: an unrecognised code is a NORMAL
  // outcome on this surface (the user is typing a code they half-remember), and it must
  // render inline on the sheet rather than as a global error banner. join_household is
  // the single source of the three distinct messages ("Invite not found." /
  // "…already used." / "…expired.").
  const joinHouseholdByCode = useCallback(async (code) => {
    const db = supabaseRef.current;
    const trimmed = (code || "").trim();
    if (!db) return { ok: false, message: "Not connected. Try again." };
    if (!trimmed) return { ok: false, message: "Enter an invite code." };

    try {
      // toUpperCase is belt-and-braces; the server normalizes with upper(trim(...)).
      const { data: joinResult, error: joinErr } = await db.rpc("join_household", {
        p_invite_code: trimmed.toUpperCase(),
      });
      if (joinErr) return { ok: false, message: joinErr.message };
      if (!joinResult?.household_id) return { ok: false, message: "Invite not found." };

      // Same two durable flags the bootstrap invite branch writes. The switch itself is
      // owned by App's durable-intent effect (ADDENDUM_reopen) — which retries through a
      // slow membership propagation — so this must not try to switch the lens itself.
      sessionStorage.setItem("just_joined_household", joinResult.household_name || "the place");
      sessionStorage.setItem("just_joined_household_id", joinResult.household_id);

      return {
        ok: true,
        householdId: joinResult.household_id,
        householdName: joinResult.household_name || "the place",
      };
    } catch (err) {
      console.error("joinHouseholdByCode error:", err.message);
      return { ok: false, message: err.message };
    }
  }, []);

  // discardUnclaimedHousehold: the D11 cleanup (migration 042).
  //
  // "Naming is claiming." Soft-deletes the solo place bootstrap made seconds ago, but
  // ONLY while nobody has claimed it — the four-condition guard lives server-side, so
  // this call is a REQUEST, not a command, and a refusal is a normal answer.
  //
  // Never surfaces an error to the user and never blocks: it runs after a join that has
  // already succeeded, and failing the user's join because we could not tidy up would be
  // the tail wagging the dog. A missing function (un-migrated database) lands here too
  // and is equally silent — the orphan simply persists, which is untidy, not broken.
  //
  // ⚠️ CALLERS MUST WRAP THIS IN beginDeliberateLoss()/endDeliberateLoss(). It soft-
  // deletes the household that is very likely still ACTIVE, and the presence watchdog
  // would otherwise read that as an external removal and fire the "no longer a member of"
  // notice — the exact scary-but-wrong message this whole spec exists to design away.
  const discardUnclaimedHousehold = useCallback(async (householdId) => {
    const db = supabaseRef.current;
    if (!db || !householdId) return false;
    try {
      const { data, error: discardErr } = await db.rpc("discard_unclaimed_household", {
        p_household_id: householdId,
      });
      if (discardErr) {
        console.error("discardUnclaimedHousehold error:", discardErr.message);
        return false;
      }
      return data?.discarded === true;
    } catch (err) {
      console.error("discardUnclaimedHousehold error:", err.message);
      return false;
    }
  }, []);

  const renameHousehold = useCallback(async (newName) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh) return false;
    const trimmed = (newName || "").trim();
    if (!trimmed) return false;
    try {
      const { error: renameErr } = await db
        .from("households").update({ name: trimmed }).eq("id", hh.id);
      if (renameErr) throw renameErr;
      const updated = { ...hh, name: trimmed };
      setHousehold(updated);
      householdRef.current = updated;
      return true;
    } catch (err) {
      console.error("renameHousehold error:", err.message);
      setError(`Could not rename place: ${err.message}`);
      return false;
    }
  }, []);

  // ─────────────────────────────────────────────────────────────
  // Meals (migration 025) — add-path data layer.
  //
  // fetchMeals:        live meals for the active household, each with its
  //                    live ingredient rows (joined to catalog_items for
  //                    name/category so the Browse lens can render them).
  // createMeal:        insert a meal + its ingredient rows. Returns the
  //                    new meal id (or null on failure).
  // addMealToList:     fold a meal's ingredients into the shared list via
  //                    the add_meal_to_list RPC (increment-or-resurrect +
  //                    provenance). Ensures an open planned cycle first,
  //                    for parity with the manual add path.
  // fetchMealProvenance: which meal(s) put each live list item on the list,
  //                    keyed by catalog_item_id → [{mealId, name}]. length
  //                    > 1 is the "Multiple meals" badge.
  // ─────────────────────────────────────────────────────────────
  const fetchMeals = useCallback(async () => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh) return [];
    const { data, error: err, status: mealsStatus } = await db
      .from("meals")
      // 055/056: kind + from_meal_ids ride along; 061: occasion (the library's
      // Good-for tags, ordered). This read returns EVERY kind — the
      // board's meal lookup must include no-shop rows (leftovers, out). The
      // library set is kind === 'meal' only, split from this array by the
      // caller (libraryMeals in App); miss that and Leftovers rows show up as
      // recipes. One read, not two, because both consumers poll on the same tick.
      .select("id, name, kind, from_meal_ids, occasion, attribution, base_servings, instructions, created_by, created_at, meal_ingredients(id, catalog_item_id, quantity_per_serving, on_hand, deleted_at, catalog_items(name, category))")
      .eq("household_id", hh.id)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });
    if (err) {
      if (stopOnAuthFailure(err, mealsStatus)) return [];
      if (classifyFetchError(err) === 'transient') { reportTransientFailure(); } else { failWith("meals", `Could not load meals: ${err.message}`); }
      return [];
    }
    reportSuccess();
    clearErrorFrom("meals");
    // The embed doesn't filter soft-deleted ingredients — drop them here.
    return (data || []).map((m) => ({
      ...m,
      meal_ingredients: (m.meal_ingredients || []).filter((mi) => mi.deleted_at == null),
    }));
  }, [reportTransientFailure, reportSuccess, failWith, clearErrorFrom]);

  // `instructions` (migration 043) is optional and defaults to null. NOT AI-only: a
  // hand-made meal can carry steps too, and nothing in the schema records the author.
  // Empty string is coerced to null so "no steps" is one value, not two.
  // `occasion` (061) is the ordered Good-for list; normalised here so a bad value
  // never reaches the CHECK. Omitted → '{}' (untagged), the column default.
  // `attribution` (062) is the free-text "From" credit; empty saves NULL, never ''.
  const createMeal = useCallback(async ({ name, baseServings = 1, instructions = null, ingredients = [], occasion = [], attribution = null }) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh) return null;
    const trimmed = (name || "").trim();
    if (!trimmed) { setError("A meal needs a name."); return null; }
    try {
      const { data: meal, error: mErr } = await db
        .from("meals")
        .insert({
          household_id: hh.id,
          name: trimmed,
          base_servings: baseServings,
          instructions: (instructions || "").trim() || null,
          occasion: normalizeOccasion(occasion),
          attribution: (attribution || "").trim() || null,
          created_by: internalUserIdRef.current,
        })
        .select("id")
        .single();
      if (mErr) throw mErr;

      const rows = (ingredients || [])
        .filter((i) => i.catalog_item_id && Number(i.quantity_per_serving) > 0)
        .map((i) => ({
          meal_id: meal.id,
          catalog_item_id: i.catalog_item_id,
          quantity_per_serving: Number(i.quantity_per_serving),
          // 044. Note the filter above keeps quantity > 0: an on-hand row carries its
          // REAL recipe quantity, never 0, which is exactly what makes it addable later
          // without asking for the number again.
          on_hand: !!i.on_hand,
        }));
      if (rows.length > 0) {
        const { error: iErr } = await db.from("meal_ingredients").insert(rows);
        if (iErr) throw iErr;
      }
      reportSuccess();
      return meal.id;
    } catch (err) {
      console.error("createMeal error:", err.message);
      setError(`Could not create meal: ${err.message}`);
      return null;
    }
  }, [reportSuccess]);

  // updateMeal: rename/re-scale the meal row, then BLANKET-REPLACE its
  // ingredients — soft-delete every live meal_ingredients row and reinsert the
  // draft fresh. Safe because meal_ingredients.id is referenced by nothing:
  // list_item_meals keys on meal_id, not meal_ingredient_id, so no id-matching
  // or diffing is required. Plain writes, mirroring createMeal — deliberately
  // NOT an RPC (see SPEC_create_meal_ui.md): edit is household-owned,
  // RLS-protected, and carries no cross-cutting concern like the advisory lock
  // or cycle resolution that justify add_meal_to_list's SECURITY DEFINER shape.
  // The NARROW occasion write (Meal Library visual v2, Amendment A5; approved by
  // Dan 2026-10-16): the "Set meal type" picker sets `occasion` and nothing else.
  // Same table, same `meals_update` policy (025, is_member_of on using + with
  // check), same grants, same normaliser as Edit Meal — no RPC, no new grant.
  // Deliberately NOT routed through updateMeal, which takes the whole payload
  // and rewrites every meal_ingredients row on each save. Returns true / false;
  // the caller owns the optimistic update, the revert and the quiet toast, so
  // no setError here (that would raise the shared error toast on top of it).
  const setMealOccasion = useCallback(async (mealId, occasion) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh || !mealId) return false;
    try {
      const { error } = await db
        .from("meals")
        .update({ occasion: normalizeOccasion(occasion) })
        .eq("id", mealId)
        .eq("household_id", hh.id); // belt-and-suspenders; RLS already scopes this
      if (error) throw error;
      reportSuccess();
      return true;
    } catch (err) {
      console.error("setMealOccasion error:", err.message);
      return false;
    }
  }, [reportSuccess]);

  const updateMeal = useCallback(async (mealId, { name, baseServings = 1, instructions = null, ingredients = [], occasion = [], attribution = null }) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh || !mealId) return false;
    const trimmed = (name || "").trim();
    if (!trimmed) { setError("A meal needs a name."); return false; }
    try {
      const { error: mErr } = await db
        .from("meals")
        .update({ name: trimmed, base_servings: baseServings, instructions: (instructions || "").trim() || null, occasion: normalizeOccasion(occasion), attribution: (attribution || "").trim() || null })
        .eq("id", mealId)
        .eq("household_id", hh.id); // belt-and-suspenders; RLS already scopes this
      if (mErr) throw mErr;

      // What the meal needed BEFORE this edit — so the ingredients the edit drops can come
      // off the list below (2026-10-19, the meal-edit link gap).
      const { data: before, error: bErr } = await db
        .from("meal_ingredients")
        .select("catalog_item_id")
        .eq("meal_id", mealId)
        .is("deleted_at", null);
      if (bErr) throw bErr;

      const { error: dErr } = await db
        .from("meal_ingredients")
        .update({ deleted_at: new Date().toISOString() })
        .eq("meal_id", mealId)
        .is("deleted_at", null);
      if (dErr) throw dErr;

      const rows = (ingredients || [])
        .filter((i) => i.catalog_item_id && Number(i.quantity_per_serving) > 0)
        .map((i) => ({
          meal_id: mealId,
          catalog_item_id: i.catalog_item_id,
          quantity_per_serving: Number(i.quantity_per_serving),
          on_hand: !!i.on_hand,   // 044 — survives the delete-and-reinsert rewrite
        }));
      if (rows.length > 0) {
        const { error: iErr } = await db.from("meal_ingredients").insert(rows);
        if (iErr) throw iErr;
      }
      // An ingredient the meal no longer needs (Dan, 2026-10-19 — today's Remove rule, per
      // ingredient): pending comes off Shop (a shared item keeps the other meal's claim);
      // in-cart stays until wrap-up, unlinked. The SAME path as Remove, scoped to the
      // dropped catalog items. Ingredients ADDED in an edit are never put on Shop. The
      // recipe is saved either way: a failure here is a RUM warning, never the edit's error.
      const dropped = droppedIngredientIds(before, rows);
      if (dropped.length > 0) {
        try {
          await takeMealOffListAndRelease(db, hh, mealId, { catalogItemIds: dropped, path: "edit" });
          await loadListItems(db, hh.id);
        } catch (rErr) {
          warnLinkRelease(rErr, { householdId: hh.id, mealId, path: "edit", links: dropped.length });
        }
      }
      reportSuccess();
      return true;
    } catch (err) {
      console.error("updateMeal error:", err.message);
      setError(`Could not update meal: ${err.message}`);
      return false;
    }
  // loadListItems / takeMealOffListAndRelease are stable hook-scope functions (use refs),
  // the same pattern removeMealFromList and addMealToList document.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportSuccess]);

  // ─────────────────────────────────────────────────────────────
  // removeMealFromList: reverses a meal's footprint on the CURRENT list
  // without touching the recipe. "I planned Pizza, I want Tacos instead"
  // — Pizza survives on PLAN, just unplanned.
  //
  // This is deleteMeal's reconciliation block, extracted verbatim so the
  // two share ONE mechanism rather than a copy that can drift. deleteMeal
  // is now this plus a soft-delete of the meals row.
  //
  // Buildable only because 039 added quantity_contributed: before it,
  // removal could not tell "this item is here only because of Pizza" from
  // "here because of Pizza AND Taco Night". That was the standing
  // 2026-07-28 open question.
  //
  // Guarantees, all inherited unchanged:
  //  - SHARED ingredients survive to the extent another meal needs them.
  //    Each item is decremented by exactly THIS meal's recorded share, so
  //    the other meal's contribution is never read and never written.
  //  - BOUGHT items are NEVER touched — only status='pending' rows are
  //    candidates. The sacred-list guarantee. Their LINK to this meal is
  //    released, though (2026-10-19, replanning starts over): the row stays in
  //    the cart as a plain item, and a later placement of this meal starts
  //    clean (src/lib/mealLinks.js).
  //  - The meal's list_item_meals link is dropped per removed item.
  //    remove_list_item SOFT-deletes, so the FK's cascade does not fire
  //    and the link must go explicitly or a later resurrect revives it.
  //
  // ⚠️ INHERITED EDGE CASE, not introduced here: if someone manually
  // stepped an ingredient BELOW this meal's contribution, the subtraction
  // goes non-positive and the item is removed outright — which can
  // under-serve another meal that still needed it. Accepted tradeoff,
  // already shipped and dev-verified via deleteMeal.
  // ─────────────────────────────────────────────────────────────
  // The one path off the list (src/lib/mealLinks.js takeMealOffList), plus the in-cart
  // link release; the caller reloads the list. A plain function so updateMeal — defined above —
  // shares it with removeMealFromList: an edit that drops ingredients takes them off the
  // list the same way a Remove does, scoped to those catalog items.
  async function takeMealOffListAndRelease(db, hh, mealId, { catalogItemIds = null, path = "remove" } = {}) {
    const links = await takeMealOffList(db, { householdId: hh.id, mealId, catalogItemIds });
    // In-cart rows: only this meal's link goes; the row, its quantity and its
    // status are untouched. A failure warns and leaves today's behaviour.
    await releaseLinksOrWarn(db, {
      householdId: hh.id, mealId, path,
      listItemIds: linksToRelease(links, { onlyBought: true }),
    });
  }

  const removeMealFromList = useCallback(async (mealId) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh || !mealId) return false;
    try {
      await takeMealOffListAndRelease(db, hh, mealId, { path: "remove" });

      await loadListItems(db, hh.id);
      reportSuccess();
      return true;
    } catch (err) {
      console.error("removeMealFromList error:", err.message);
      setError(`Could not remove meal from list: ${err.message}`);
      return false;
    }
  // loadListItems is a stable hook-scope function (uses refs); matches the
  // ref-based pattern used by addMealToList et al.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportSuccess]);

  // ─────────────────────────────────────────────────────────────
  // decrementMealBatch: undo exactly ONE add. The mirror of tapping Add
  // again, which is why "+" is addMealToList unchanged.
  //
  // ⚠️ THE WHOLE BODY IS AN RPC CALL, AND THAT IS THE POINT (041).
  // The first version of this function amended the ledger from the
  // client:
  //     db.from("list_item_meals").update({ add_count, quantity_contributed })
  // list_item_meals has SELECT/INSERT/DELETE policies only — NO UPDATE
  // policy, deliberately (025_meals.sql:13, "a provenance linkage row is
  // delete-and-reinserted, never updated"). The UPDATE therefore matched
  // zero rows and raised NO error, because RLS filters silently. Since
  // list_items.quantity DID update, the shopping list moved correctly
  // while the card's count froze — and 1→0 still looked fine because
  // that path DELETEs, which a policy does cover. Half-working, no error,
  // shipped to dev in e9c1bf8.
  //
  // The per-ingredient loop, the meal_ingredients read and the ledger
  // writes now all live server-side in decrement_meal_from_list, running
  // SECURITY DEFINER exactly as add_meal_to_list's writes to this same
  // table already do. Do NOT reintroduce a client-side write here — the
  // fix is the trust boundary, not the syntax.
  // ─────────────────────────────────────────────────────────────
  const decrementMealBatch = useCallback(async (mealId) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh || !mealId) return false;
    try {
      const { error } = await db.rpc("decrement_meal_from_list", { p_meal_id: mealId });
      if (error) throw error;
      await loadListItems(db, hh.id);
      reportSuccess();
      return true;
    } catch (err) {
      console.error("decrementMealBatch error:", err.message);
      setError(`Could not adjust meal: ${err.message}`);
      return false;
    }
  // loadListItems is a stable hook-scope function (uses refs); matches the
  // ref-based pattern used by addMealToList et al.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportSuccess]);

  // ─────────────────────────────────────────────────────────────
  // deleteMeal: removes the RECIPE itself. SOFT-delete
  // (`meals.deleted_at`) — decided 2026-08-20: it matches the schema's
  // deleted_at contract and preserves the record of which meal put an
  // item on the list, which a hard delete destroys.
  //
  // Now a thin wrapper. The SEQUENCE remains load-bearing: reverse the
  // meal's list footprint FIRST, then stamp deleted_at — so deleting a
  // recipe can never strand active items whose provenance points at
  // nothing. Every guarantee lives in removeMealFromList above; this
  // adds exactly one write.
  //
  // Bails if the reconciliation failed rather than soft-deleting anyway:
  // a deleted recipe whose items were never reversed is the stranded
  // state the sequence exists to prevent.
  // ─────────────────────────────────────────────────────────────
  const deleteMeal = useCallback(async (mealId) => {
    const removed = await removeMealFromList(mealId);
    if (!removed) return false;
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh) return false;
    try {
      const { error: mErr } = await db
        .from("meals")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", mealId)
        .eq("household_id", hh.id) // belt-and-suspenders; RLS already scopes this
        .is("deleted_at", null);
      if (mErr) throw mErr;
      // A deleted meal must not stay the ghost head of the queue (050): close
      // its placement as skipped. Best-effort — the meal is gone regardless.
      try {
        const cur = placementsRef.current[mealId];
        if (cur && !cur.cookedAt && !cur.skippedAt) await closePlacement(db, hh, mealId, "skipped_at");
      } catch (pErr) { console.warn("deleteMeal placement close (non-fatal):", pErr.message); }
      return true;
    } catch (err) {
      console.error("deleteMeal error:", err.message);
      setError(`Could not delete meal: ${err.message}`);
      return false;
    }
  // closePlacement is a stable hook-scope function (uses refs).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeMealFromList]);

  // findCatalogMatch: the existing-row short-circuit shared by the resolver and
  // the Save-time materializer. Exact-normalised match against the live catalog,
  // then against the HIDDEN set — a hidden match resolves to the existing row
  // rather than forking a duplicate (mirrors updateQty's resolver hardening).
  const findCatalogMatch = useCallback((trimmed) => {
    const existing = catalogRef.current[trimmed]
      || Object.values(catalogRef.current).find((it) => normCatalogName(it.name) === normCatalogName(trimmed));
    if (existing) return existing;
    const hiddenMatch = hiddenCatalogItemsRef.current.find((it) => normCatalogName(it.name) === normCatalogName(trimmed));
    if (hiddenMatch) {
      catalogRef.current = { ...catalogRef.current, [hiddenMatch.name]: hiddenMatch };
      return hiddenMatch;
    }
    return null;
  }, []);

  // createCatalogItem: resolve a typed name to a catalog row — an existing one
  // if there is a match, otherwise a client-only PENDING placeholder. It no
  // longer writes anything. The meal builder stages the result into a draft that
  // lives entirely in local state until Save, and a catalog row minted the moment
  // a category pill was tapped outlived every Cancel as an orphan nobody had
  // claimed (SPEC_defer_catalog_write). The write moved to
  // materializePendingIngredients, which runs only when the meal is committed.
  // Still NOTHING ELSE: updateQty's insert path (the other caller of
  // insert_custom_catalog_item) also writes a list_items row, so it cannot be
  // reused here without putting every new meal ingredient onto the shopping list.
  const createCatalogItem = useCallback(async (name, categoryName) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    const trimmed = (name || "").trim();
    if (!db || !hh || !trimmed) return null;
    const match = findCatalogMatch(trimmed);
    if (match) return match;
    return {
      id: PENDING_CATALOG_PREFIX + normCatalogName(trimmed),
      name: trimmed,
      category: categoryName || "Household",
      is_global: false,
    };
  }, [findCatalogMatch]);

  // materializePendingIngredients: the deferred write. Walks a draft's staged
  // rows and, for each one still carrying a pending placeholder, mints the real
  // catalog row via insert_custom_catalog_item and substitutes its id. Returns the
  // resolved rows, or null if any insert failed — the caller must NOT commit the
  // meal on null, so the sheet stays open for a retry.
  //
  // Match-first, deliberately: a retry after a mid-loop failure finds the rows
  // the failed attempt already created and reuses them instead of minting
  // duplicates. That contains the spec's known residual risk (rows created by a
  // Save that then fails, orphaned only if the user cancels afterwards) rather
  // than solving it with a transactional RPC — accepted at build, per the spec.
  const materializePendingIngredients = useCallback(async (ingredients) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh) return null;
    const resolved = [];
    for (const ing of ingredients || []) {
      if (!isPendingCatalogId(ing.catalog_item_id)) { resolved.push(ing); continue; }
      const trimmed = (ing.name || "").trim();
      let item = findCatalogMatch(trimmed);
      if (!item) {
        const category = ing.category || "Household";
        try {
          const { data: insertedId, error: insertErr } = await db
            .rpc("insert_custom_catalog_item", {
              p_name: trimmed,
              p_category: category,
              p_household_id: hh.id,
              p_created_by: internalUserIdRef.current,
            });
          if (insertErr) throw insertErr;
          item = { id: insertedId, name: trimmed, category, is_global: false, household_id: hh.id };
          catalogRef.current = { ...catalogRef.current, [trimmed]: item };
          setCatalogMap((prev) => ({ ...prev, [trimmed]: item }));
          reportSuccess();
        } catch (err) {
          console.error("materializePendingIngredients error:", err.message);
          setError(`Could not add "${trimmed}": ${err.message}`);
          return null;
        }
      }
      resolved.push({ ...ing, catalog_item_id: item.id });
    }
    return resolved;
  }, [findCatalogMatch, reportSuccess]);

  // requestMealSuggestion: ask the meal-suggestion Edge Function for ONE meal draft.
  // Returns { name, baseServings, instructions, ingredients:[{name, quantity, unit}] }
  // or null, surfacing failure through the same setError path as everything else here.
  //
  // The catalog context is assembled from what is ALREADY IN MEMORY (catalogRef) — no
  // extra round trip, and no service-role read on the function side. That is the whole
  // reason the function is stateless: it can only ever see what this client already had.
  //
  // Units are included and matter: the function pins a matched ingredient to its
  // existing catalog unit so the AI cannot introduce a competing one for an item the
  // household already has.
  //
  // Deliberately does NOT call reportSuccess/reportTransientFailure. Those track
  // Supabase health for the offline banner; the Anthropic API being slow or rate-limited
  // is not evidence the database is unreachable, and folding it in would make the
  // connection indicator lie.
  //
  // Returns one of (walk defect 16, 2026-10-12 — ONE error location on the card):
  //   { draft }    — ok
  //   { reason }   — "offline" | "session_expired" | "failed": a key into GALLEY_HINTS,
  //                  shown by the SHEET in the card's hint line, never a toast; the
  //                  request text is kept for the retry
  //   null         — the person pressed Never mind (AbortError, swallowed, no copy)
  // `signal` (optional AbortSignal) is how Never mind cancels a request in flight.
  // This hook sets no error for the Galley any more: the card owns its own copy.
  const requestMealSuggestion = useCallback(async (promptText, { signal } = {}) => {
    const text = (promptText || "").trim();
    if (!text) return null;
    const getToken = getTokenRef.current;
    if (!getToken) return { reason: "session_expired" };
    try {
      const token = await getToken({ template: "supabase" });
      // Clerk was asked and came back empty: the session is gone, not the network.
      // "Try again" was the wrong instruction — retrying a dead session never works,
      // and it read as a transient glitch to the one beta tester who hit it.
      if (!token) return { reason: "session_expired" };

      const catalog = Object.values(catalogRef.current || {})
        .filter((it) => it && it.name)
        .map((it) => ({ name: it.name, category: it.category || "", unit: it.unit || "each" }));

      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/meal-suggestion`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ requestText: text, catalog }),
          signal,
        },
      );

      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        console.warn(`[galley] meal-suggestion ${res.status}:`, payload?.error || "(no body)");
        return { reason: "failed" };
      }
      // The function already validates and normalises, but it is across a network
      // boundary — a shape check here is cheap and keeps a bad payload from reaching
      // the modal's setState. Fail loudly rather than half-populating the fields.
      if (!payload || typeof payload !== "object" || Array.isArray(payload)
          || typeof payload.name !== "string" || !Array.isArray(payload.ingredients)) {
        console.warn("[galley] the suggestion came back in an unexpected shape");
        return { reason: "failed" };
      }

      // Some drafts come back with the ESCAPE SEQUENCE "\n" as two literal characters
      // rather than a real newline, so the steps rendered as `4. Simmer.\n5. Serve.`
      // in the textarea (seen on the 2026-09-01 verification walk). The prompt asks for
      // "1. ...\n2. ..." and a model can reasonably read that as text to reproduce.
      // Normalising here — at the parse boundary, once — beats asking every consumer to
      // remember, and beats a prompt tweak that would only make it rarer, not impossible.
      const unescapeSteps = (v) => (typeof v === "string" ? v : "")
        .replace(/\\r\\n/g, "\n")
        .replace(/\\n/g, "\n")
        .replace(/\\t/g, " ")
        .trim();
      const instructions = unescapeSteps(payload.instructions);
      // THE DRAFT GUARD (Dan, 2026-10-12, walk B6: a draft landed with instructions
      // "x"; the 2026-09-02 log saw the same once). The function only checks that
      // instructions are non-empty. Fewer than 2 parsed steps AND fewer than 40
      // characters is malformed: fail loudly with the plain hint, never land it.
      // The warn carries counts only, never recipe text. Thresholds: src/lib/galleyDraft.js.
      if (isMalformedGalleyDraft(instructions)) {
        const m = measureSteps(instructions);
        console.warn(`[galley] malformed draft refused: name=${JSON.stringify(payload.name)} steps=${m.steps} chars=${m.chars} ingredients=${payload.ingredients.length}`);
        return { reason: "failed" };
      }
      return { draft: { ...payload, instructions } };
    } catch (err) {
      // Never mind. Swallowed on purpose — see the note above the function. Checked by
      // name rather than `signal?.aborted` so a request that was aborted after the
      // fetch settled (in res.json()) is still recognised as the user's own cancel.
      const kind = classifyFetchFailure(err);
      if (kind === "aborted") return null;
      // Plain copy only; the browser's own text ("Load failed", "Failed to fetch")
      // is for the console (2026-10-11, the same leak the import had).
      console.error("requestMealSuggestion error:", err?.message || err);
      return { reason: kind === "offline" ? "offline" : "failed" };
    }
  }, []);

  // requestRecipeImport: read a recipe out of screenshots or pasted text through the
  // recipe-import Edge Function (SPEC_recipe_import, chunk 2). Same identity and
  // transport as requestMealSuggestion; the draft comes back in the Galley's shape
  // plus attribution / servingsAssumed / asWritten / uncertain, so MealSheet lands
  // both through one function.
  //
  // `input` is the function's own contract: { kind: "text", text } or
  // { kind: "images", images: [{ media_type, data_base64 }] }. Nothing is logged
  // here and nothing is kept — the images go up, the draft comes back (spec D5).
  //
  // Returns one of:
  //   { draft }    — ok
  //   { reason }   — a key into IMPORT_HINTS, shown by the SHEET in the card's hint
  //                  line, never a toast (walk defect 16): "not_a_recipe" |
  //                  "unreadable" (the function declined), "timeout" (504: the 75 s
  //                  model cap), "unreachable" (502), "offline", "session_expired",
  //                  "failed" (anything else — the reason is on the console)
  //   null         — the person pressed Never mind (AbortError, swallowed)
  // Every one has a next move the person can make in the card (sharper screenshot,
  // fewer of them, try again) and the staged input is kept for it.
  const requestRecipeImport = useCallback(async (input, { signal } = {}) => {
    if (!input || (input.kind !== "text" && input.kind !== "images")) return { reason: "failed" };
    const getToken = getTokenRef.current;
    if (!getToken) return { reason: "session_expired" };
    try {
      const token = await getToken({ template: "supabase" });
      if (!token) return { reason: "session_expired" };

      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/recipe-import`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify(input),
          signal,
        },
      );

      const payload = await res.json().catch(() => null);
      // The function's own clean failures (its Anthropic call is capped at 75 s with
      // no retries, so these are never the platform's bare 503/504).
      if (res.status === 504) return { reason: "timeout" };
      if (res.status === 502) return { reason: "unreachable" };
      if (!res.ok) {
        console.warn(`[galley] recipe-import ${res.status}:`, payload?.error || "(no body)");
        return { reason: "failed" };
      }
      if (payload && payload.ok === false) {
        if (payload.reason === "not_a_recipe" || payload.reason === "unreadable") return { reason: payload.reason };
        console.warn("[galley] the reader came back with an unexpected answer:", payload.reason);
        return { reason: "failed" };
      }
      if (!payload || typeof payload !== "object" || Array.isArray(payload)
          || typeof payload.name !== "string" || !Array.isArray(payload.ingredients)) {
        console.warn("[galley] the recipe came back in an unexpected shape");
        return { reason: "failed" };
      }
      // Same escape-sequence repair as the Galley path, at the same boundary. The
      // function already does this before numbering; a second pass is a no-op on a
      // clean string and a guard against a draft that slipped through.
      const unescapeSteps = (v) => (typeof v === "string" ? v : "")
        .replace(/\\r\\n/g, "\n")
        .replace(/\\n/g, "\n")
        .replace(/\\t/g, " ")
        .trim();
      return { draft: { ...payload, instructions: unescapeSteps(payload.instructions) } };
    } catch (err) {
      // Thrown around fetch — classified by name, never by message, and the
      // message goes to the console only (2026-10-11: "Could not read the recipe:
      // Load failed" reached a phone that had gone offline mid-read).
      const kind = classifyFetchFailure(err);
      if (kind === "aborted") return null;                 // Never mind — silent
      console.error("requestRecipeImport error:", err?.message || err);
      if (kind === "offline") return { reason: "offline" }; // the card's hint; staged input kept
      return { reason: "failed" };
    }
  }, []);

  // includeOnHandIds: CATALOG_ITEM_IDs of on-hand ingredients to add THIS TIME
  // only (045). Deliberately a call argument rather than a stored flag — the RPC
  // does not touch meal_ingredients.on_hand, so "include this time" never quietly
  // becomes "include from now on". (removeMealIngredients — the prompt's "Remove from
  // this meal" — was deleted 2026-10-19, SPEC_all_on_hand D4: the recipe changes in Edit.)

  // ─────────────────────────────────────────────────────────────
  // Plan board placements (migration 047). Three pieces:
  //
  // loadPlacements / refreshPlacements — the household's rows as a
  //   meal_id → sort_order map. Only sets state when the map actually changed,
  //   so the 2s Plan poll does not re-render the board on every tick.
  //   refreshPlacements is the polled entry point and skips while a reorder
  //   write is in flight.
  // appendPlacement — the add-path write for a meal with no row or a CLOSED
  //   row: max+1 for the household, ready/cooked/skipped all cleared, upserted
  //   so a closed placement (cooked or skipped weeks ago) REOPENS at the end
  //   of the board rather than reviving its old slot. Non-fatal by design:
  //   the meal is on the list whether or not this row lands.
  // clearReady — an add on an OPEN card that is already Ready: the meal has
  //   a new need again, so readiness clears; the slot is kept (a stepper bump
  //   is a servings change, not a re-plan).
  // reorderBoard — renumber the board 0..n-1 in one upsert batch. Optimistic;
  //   on failure the rows are reloaded and the board snaps back.
  // markCooked / skipMeal — THE TWO EXITS. A card leaves the board by a tap,
  //   never by side effect: wrap-up, decrements and other meals' adds never
  //   close a placement. skipMeal on a to-buy card zeroes the meal's pending
  //   rows first (removeMealFromList, as built) and then closes.
  // planMeal — PLAN WITHOUT ADDING (SPEC_meal_planning_v1_board_planned.md):
  //   an open placement at max+1 and nothing else. Planning and shopping are
  //   separate acts; the board's object says a meal is planned in this period
  //   and never drives the list on its own. A meal already open is a no-op.
  // lockIn / lockInAll — the reverse: put a planned card's groceries on the
  //   list through the existing add path (an open row keeps its slot). lockInAll
  //   runs lockIn over the ids it is given, in that order, and reports the
  //   count for ONE toast — never one per meal. ("Add to Shop" in the UI since
  //   v2 — same act as adding an item, so the same word; the names stay.)
  //   Never call either on a no-shop meal: the caller filters kind === 'meal'.
  // planNoShop — v2 (SPEC_meal_planning_v2_pick_commit_cook.md, 055/057): a
  //   night that needs no groceries. Leftovers / Eating out / Something else
  //   ('other', 057) are their OWN meals rows with kind, no ingredients, placed
  //   in the same action. Kinds of plan, not states: the board's three states
  //   (Planned / To buy / Ready) belong to meals only. They never touch
  //   the list and never become Ready (052's link condition has nothing to
  //   match). × is their only exit: skipMeal also soft-deletes the meals row,
  //   so a no-shop card is one-shot and can never be re-added.
  // markCooked, since 058 — the cook_meal RPC, not a client update: it closes
  //   the open placement AND writes a meal_cooks row in one transaction (D2),
  //   with ONE timestamp for both. A null return means no open placement was
  //   closed (D3: a partner got there first, or a double tap) — the meal is
  //   cooked, just not by this tap: reload, no toast. Clearing cooked_at on the
  //   placement at re-plan (appendPlacement) is now CORRECT: the placement is
  //   the plan; the log keeps the history.
  // loadCooks — the household's meal_cooks reduced to meal_id → { lastCookedAt,
  //   count }; newest-first read, first row per meal is lastCookedAt.
  // madeBefore — the library's filter: meal ids with at least one cook in the
  //   log (D5). Re-planning no longer removes a meal from this set; the Plan
  //   welcome's copy ("Add a meal" vs "Add your first meal") follows for free.
  // upNext — the head of the open queue, for Home.
  //
  // Rows close, they don't die: there is no DELETE policy, so a client delete
  // would match zero rows and raise nothing (041) — don't write one.
  // ─────────────────────────────────────────────────────────────
  // plannedAt (063, 2026-10-18): when this placement was last (re)planned — stamped by
  // appendPlacement only; null on rows older than 063 (readers fall back to the meal's
  // last cook). Read by the recipe view's "bought for this meal" window.
  const rowToPlacement = (r) => ({ sortOrder: r.sort_order, readyAt: r.ready_at ?? null, cookedAt: r.cooked_at ?? null, skippedAt: r.skipped_at ?? null, plannedAt: r.planned_at ?? null });
  const samePlacement = (a, b) => !!a && !!b && a.sortOrder === b.sortOrder && a.readyAt === b.readyAt && a.cookedAt === b.cookedAt && a.skippedAt === b.skippedAt && a.plannedAt === b.plannedAt;
  const setPlacementsBoth = (next) => { placementsRef.current = next; setPlacements(next); };
  // §Polling discipline — placements backoff. The 2s meal tick keeps ticking
  // (it is App.js's interval); this ref tells the POLLED entry (refreshPlacements)
  // to sit out until `until`. Each non-auth failure doubles the wait,
  // 2s → 4s → 8s → 16s → 30s cap; the first success resets it. Backoff is for
  // network/5xx only — an auth failure never retries (stopOnAuthFailure).
  const PLACEMENTS_BACKOFF_BASE_MS = 2000;
  const PLACEMENTS_BACKOFF_CAP_MS = 30000;
  const placementsBackoffRef = useRef({ delayMs: PLACEMENTS_BACKOFF_BASE_MS, until: 0 });
  async function loadCooks(db, hh) {
    const { data, error: err, status: ckStatus } = await db
      .from("meal_cooks")
      .select("meal_id, cooked_at")
      .eq("household_id", hh.id)
      .order("cooked_at", { ascending: false })
      .limit(1000);   // a known bound (years of cooking); a summary view replaces it when it matters
    if (err) {
      if (!stopOnAuthFailure(err, ckStatus)) console.warn("loadCooks (non-fatal):", err.message);
      return;
    }
    if (householdRef.current?.id !== hh.id) return;   // switched households mid-read
    setCooks(cooksFromRows(data));   // always rebuilt from the server's rows (src/lib/cooks.js)
    setCooksLoadedFor(hh.id);
  }

  async function loadPlacements(db, hh) {
    const { data, error: err, status: plStatus } = await db
      .from("meal_placements")
      .select("meal_id, sort_order, ready_at, cooked_at, skipped_at, planned_at")
      .eq("household_id", hh.id);
    if (err) {
      if (stopOnAuthFailure(err, plStatus)) return;
      const b = placementsBackoffRef.current;
      b.delayMs = Math.min(b.delayMs * 2, PLACEMENTS_BACKOFF_CAP_MS);
      b.until = Date.now() + b.delayMs;
      console.error("loadPlacements error:", err.message, `— next try in ${b.delayMs / 1000}s`);
      return;
    }
    placementsBackoffRef.current = { delayMs: PLACEMENTS_BACKOFF_BASE_MS, until: 0 };
    const next = {};
    (data || []).forEach((r) => { next[r.meal_id] = rowToPlacement(r); });
    const prev = placementsRef.current;
    const prevKeys = Object.keys(prev);
    const same = prevKeys.length === Object.keys(next).length
      && prevKeys.every((k) => samePlacement(next[k], prev[k]));
    if (same) return;
    // 058: a changed cookedAt anywhere (a partner's cook, a re-plan clearing
    // one, this device's own cook confirmed) is the one signal that the log
    // moved — re-read it once, off the poll that already ran. Also fires on the
    // first load after a switch (prev empty), a cheap duplicate of Effect 2's read.
    const cookChanged = Object.keys(next).some((k) => next[k].cookedAt !== (prev[k]?.cookedAt ?? null))
      || prevKeys.some((k) => !next[k] && prev[k].cookedAt);
    setPlacementsBoth(next);
    if (cookChanged) await loadCooks(db, hh);
  }

  const refreshPlacements = useCallback(async () => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh) return;
    if (reorderPendingRef.current > 0) return;
    if (Date.now() < placementsBackoffRef.current.until) return;   // backing off after a failure
    await loadPlacements(db, hh);
  // loadPlacements is a stable hook-scope function (uses refs).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function appendPlacement(db, hh, mealId) {
    const { data: top, error: mErr } = await db
      .from("meal_placements")
      .select("sort_order")
      .eq("household_id", hh.id)
      .order("sort_order", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (mErr) throw mErr;
    const sortOrder = (top?.sort_order ?? -1) + 1;
    // 063: planned_at is stamped HERE and only here — the one write that opens (or
    // reopens) a placement. Reorder, Ready, cook and skip never touch it.
    const plannedAt = new Date().toISOString();
    const { error: uErr } = await db
      .from("meal_placements")
      .upsert({
        household_id: hh.id,
        meal_id: mealId,
        sort_order: sortOrder,
        ready_at: null,
        cooked_at: null,
        skipped_at: null,
        planned_at: plannedAt,
        updated_at: plannedAt,
        updated_by: internalUserIdRef.current,
      }, { onConflict: "household_id,meal_id" });
    if (uErr) throw uErr;
    setPlacementsBoth({ ...placementsRef.current, [mealId]: { sortOrder, readyAt: null, cookedAt: null, skippedAt: null, plannedAt } });
  }

  async function clearReady(db, hh, mealId) {
    const { error: err } = await db
      .from("meal_placements")
      .update({ ready_at: null, updated_at: new Date().toISOString(), updated_by: internalUserIdRef.current })
      .eq("household_id", hh.id)
      .eq("meal_id", mealId);
    if (err) throw err;
    const cur = placementsRef.current[mealId];
    if (cur) setPlacementsBoth({ ...placementsRef.current, [mealId]: { ...cur, readyAt: null } });
  }

  // One-column close for the two exits. Optimistic: the card leaves on this
  // render; on failure the rows are reloaded and it comes back.
  async function closePlacement(db, hh, mealId, column) {
    const prev = placementsRef.current;
    const cur = prev[mealId];
    const stamp = new Date().toISOString();
    if (cur) setPlacementsBoth({ ...prev, [mealId]: { ...cur, [column === "cooked_at" ? "cookedAt" : "skippedAt"]: stamp } });
    const { error: err } = await db
      .from("meal_placements")
      .update({ [column]: stamp, updated_at: stamp, updated_by: internalUserIdRef.current })
      .eq("household_id", hh.id)
      .eq("meal_id", mealId);
    if (err) { await loadPlacements(db, hh); throw err; }
  }

  const markCooked = useCallback(async (mealId) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh || !mealId) return false;
    // Optimistic close (the card mutes at once; the ✓ Cooked afterglow is unchanged).
    const prev = placementsRef.current;
    const cur = prev[mealId];
    if (cur) setPlacementsBoth({ ...prev, [mealId]: { ...cur, cookedAt: new Date().toISOString() } });
    const { data: cookedAt, error: err } = await db.rpc("cook_meal", { p_household_id: hh.id, p_meal_id: mealId });
    if (err) {
      await loadPlacements(db, hh);
      console.error("markCooked error:", err.message);
      setError(`Could not mark cooked: ${err.message}`);
      return false;
    }
    // The placement is closed (by this tap, or a partner's a moment ago): the meal
    // lets go of every live link it holds — at R1 the whole cart — so a re-plan
    // starts over (src/lib/mealLinks.js). The rows stay where they are.
    let live = null;
    try {
      live = await fetchLiveMealLinks(db, { householdId: hh.id, mealId });
    } catch (e) {
      warnLinkRelease(e, { householdId: hh.id, mealId, path: "cook-read", links: 0 });
    }
    if (live) await releaseLinksOrWarn(db, { householdId: hh.id, mealId, path: "cook", listItemIds: linksToRelease(live) });
    if (cookedAt == null) {
      // D3: nothing was closed — a partner's tap or a double tap already cooked
      // it. The truth is on the server; read it back. No toast: the meal is cooked.
      // No cook of OURS to rate or photograph (S3: no id, no controls).
      setCookIdsByMeal((prev) => ({ ...prev, [mealId]: null }));
      await loadPlacements(db, hh);
      await loadCooks(db, hh);
      reportSuccess();
      return true;
    }
    // The server's timestamp replaces the optimistic one (it is also the log row's).
    const now = placementsRef.current;
    if (now[mealId]) setPlacementsBoth({ ...now, [mealId]: { ...now[mealId], cookedAt } });
    // S3: the new cook's id, found by the returned timestamp (src/lib/cooks.js).
    const found = await resolveCookId(db, { householdId: hh.id, mealId, cookedAt });
    setCookIdsByMeal((prev) => ({ ...prev, [mealId]: found.id }));
    // The count is RE-READ, never incremented (2026-10-19): the placements poll may already
    // have re-read the log mid-cook (cookChanged → loadCooks), and a +1 on top of that
    // counted one cook twice ("5 times" over 4 rows). The placement stays optimistic.
    await loadCooks(db, hh);
    reportSuccess();
    return true;
  // loadPlacements / loadCooks are stable hook-scope functions (use refs).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportSuccess]);

  // zeroList: the caller's knowledge that the card is to-buy (ready_at null).
  // Zeroing first, closing second: if the list write fails the card stays and
  // says so, rather than leaving a closed placement over live pending rows.
  const skipMeal = useCallback(async (mealId, { zeroList = true } = {}) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh || !mealId) return false;
    if (zeroList) { const ok = await removeMealFromList(mealId); if (!ok) return false; }
    try { await closePlacement(db, hh, mealId, "skipped_at"); }
    catch (err) { console.error("skipMeal error:", err.message); setError(`Could not skip meal: ${err.message}`); return false; }
    // 055: a no-shop card (kind leftovers | out) is one-shot — its meals row
    // goes with the placement so nothing can re-add it. The kind check is IN
    // the predicate, so this write matches zero rows for a real recipe and
    // needs no lookup. Best-effort: the card is gone either way (boardMeals
    // reads from `meals`, which excludes deleted rows, and the placement is
    // closed); a stranded no-shop row is inert and never reaches the library
    // (kind filter).
    try {
      const { error: dErr } = await db
        .from("meals")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", mealId)
        .eq("household_id", hh.id)
        .neq("kind", "meal")
        .is("deleted_at", null);
      if (dErr) throw dErr;
    } catch (dErr) { console.warn("skipMeal no-shop soft-delete (non-fatal):", dErr.message); }
    reportSuccess();
    return true;
  // closePlacement is a stable hook-scope function (uses refs).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportSuccess, removeMealFromList]);

  const planMeal = useCallback(async (mealId) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh || !mealId) return false;
    const cur = placementsRef.current[mealId];
    if (cur && !cur.cookedAt && !cur.skippedAt) return true;   // already on the board
    try { await appendPlacement(db, hh, mealId); reportSuccess(); return true; }
    catch (err) { console.error("planMeal error:", err.message); setError(`Could not plan meal: ${err.message}`); return false; }
  // appendPlacement is a stable hook-scope function (uses refs).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportSuccess]);

  // planNoShop(kind, name?, fromMealIds?) → meal id | null. Insert the meals
  // row, then place it at max+1 — one client action. The name defaults to the
  // card's label so the board and Home always have something to say.
  // fromMealIds (056): 0..n source meals for leftovers; stored null when empty
  // so the column has one "none", never [].
  const planNoShop = useCallback(async (kind, name = null, fromMealIds = null) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh) return null;
    if (kind !== "leftovers" && kind !== "out" && kind !== "other") { console.error("planNoShop: bad kind", kind); return null; }
    const label = (name || "").trim() || (kind === "leftovers" ? "Leftovers" : kind === "out" ? "Eating out" : "Something else");
    try {
      const { data: meal, error: mErr } = await db
        .from("meals")
        .insert({
          household_id: hh.id,
          name: label,
          kind,
          from_meal_ids: kind === "leftovers" && Array.isArray(fromMealIds) && fromMealIds.length > 0 ? fromMealIds : null,
          base_servings: 1,
          created_by: internalUserIdRef.current,
        })
        .select("id")
        .single();
      if (mErr) throw mErr;
      await appendPlacement(db, hh, meal.id);
      reportSuccess();
      return meal.id;
    } catch (err) {
      console.error("planNoShop error:", err.message);
      setError(`Could not add ${label} to the board: ${err.message}`);
      return null;
    }
  // appendPlacement is a stable hook-scope function (uses refs).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportSuccess]);

  // Made before (D5) — see the block comment above. The cook log, not the
  // placement's cooked_at, so a re-plan cannot empty it.
  const madeBefore = useMemo(() => new Set(Object.keys(cooks)), [cooks]);

  // The head of the open queue — what Home shows as "Up next" (never
  // "Tonight": that is earned in Days v2 when planned_for = today).
  const upNext = useMemo(() => {
    let head = null;
    Object.entries(placements).forEach(([mealId, p]) => {
      if (p.cookedAt || p.skippedAt) return;
      if (!head || p.sortOrder < head.sortOrder) head = { mealId, sortOrder: p.sortOrder, readyAt: p.readyAt };
    });
    return head;
  }, [placements]);

  const reorderBoard = useCallback(async (orderedMealIds) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh || !orderedMealIds?.length) return false;
    const prev = placementsRef.current;
    const next = { ...prev };
    orderedMealIds.forEach((id, i) => { next[id] = { ...(prev[id] || { readyAt: null, cookedAt: null, skippedAt: null }), sortOrder: i }; });
    // Optimistic: the board re-sorts on this render. A meal that had no row
    // (legacy data, a race) is in orderedMealIds too, so it gets one here.
    // Only sort_order is written: the upsert's conflict update touches the
    // columns in the payload and leaves the queue timestamps alone.
    setPlacementsBoth(next);
    reorderPendingRef.current += 1;
    let ok = false;
    try {
      const now = new Date().toISOString();
      const rows = orderedMealIds.map((id, i) => ({
        household_id: hh.id,
        meal_id: id,
        sort_order: i,
        updated_at: now,
        updated_by: internalUserIdRef.current,
      }));
      const { error: err } = await db
        .from("meal_placements")
        .upsert(rows, { onConflict: "household_id,meal_id" });
      if (err) throw err;
      reportSuccess();
      ok = true;
    } catch (err) {
      console.error("reorderBoard error:", err.message);
    }
    reorderPendingRef.current -= 1;
    if (!ok) await loadPlacements(db, hh);
    return ok;
  // loadPlacements is a stable hook-scope function (uses refs).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportSuccess]);

  // The placement decision on add (050): no row or a CLOSED row → reopen at the
  // end of the board (appendPlacement); an OPEN row → keep its slot — a stepper
  // bump is a servings change, not a re-plan — and if it was Ready, clear
  // that, because the meal has a new need again.
  const addMealToList = useCallback(async (mealId, servings = 1, includeOnHandIds = []) => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh || !mealId) return 0;
    try {
      // The RPC resolves the open cycle SERVER-SIDE (opening one if none) — do
      // NOT auto-open here. A client auto-open is redundant and was itself a
      // double-open-cycle contributor. Pass activeCycleRef only as a hint; the
      // RPC ignores it unless it names a genuinely-open cycle.
      // DXA: which catalog ids were LIVE before the RPC — read from server
      // state so the per-ingredient emission below can tell a fresh row (new or
      // revived tombstone) from a bump of a row already on the list.
      const liveBefore = new Set(listRowsRef.current.map((r) => r.catalogItemId));
      const { data: count, error: err } = await db.rpc("add_meal_to_list", {
        p_meal_id: mealId,
        p_servings: servings,
        p_cycle_id: activeCycleRef.current?.id || null,
        p_include_on_hand_ids: includeOnHandIds || [],
      });
      if (err) throw err;
      // The placement is written in the SAME action as the add so the card has
      // a slot the moment it appears (047). Non-fatal: log, never toast.
      try {
        const cur = placementsRef.current[mealId];
        const open = !!cur && !cur.cookedAt && !cur.skippedAt;
        if (!open) await appendPlacement(db, hh, mealId);
        else if (cur.readyAt) await clearReady(db, hh, mealId);
        // 064 (SPEC_all_on_hand): a count of 0 is the all-on-hand add — the RPC stamped the
        // open placement ready. Re-read so the stamp shows on every surface at once.
        if (!count) await loadPlacements(db, hh);
      } catch (pErr) { console.warn("placement on add (non-fatal):", pErr.message); }
      // Re-sync the active cycle (the RPC may have opened one) + refresh the
      // list so quantities and provenance reflect immediately.
      await loadActiveCycle(db, hh.id);
      await loadListItems(db, hh.id);
      reportSuccess();
      // DXA event, source: 'meal' — one item_added_to_list per ingredient row
      // the RPC put ON the list fresh (new or revived), never for one it merely
      // bumped. The RPC returns only a count, so the ingredient list is read
      // here, telemetry-only, with the same on_hand predicate the RPC applies
      // (044): on_hand rows count only when included this time. Own try/catch,
      // console.warn only — never blocks or throws into the add.
      try {
        const includeSet = new Set(includeOnHandIds || []);
        const { data: ings, error: iErr } = await db
          .from("meal_ingredients")
          .select("catalog_item_id, on_hand, catalog_items(category, is_global)")
          .eq("meal_id", mealId)
          .is("deleted_at", null);
        if (iErr) throw iErr;
        const liveNow = new Set(listRowsRef.current.map((r) => r.catalogItemId));
        for (const ing of ings || []) {
          const cid = ing.catalog_item_id;
          if (ing.on_hand && !includeSet.has(cid)) continue;   // skipped by the RPC
          if (liveBefore.has(cid) || !liveNow.has(cid)) continue; // bump, or not landed
          tracer.startSpan("item_added_to_list", {
            attributes: {
              catalog_item_id: cid,
              category: ing.catalog_items?.category ?? null,
              is_custom: ing.catalog_items ? !ing.catalog_items.is_global : null,
              household_id: hh.id,
              source: "meal",
              meal_id: mealId,
            },
          }).end();
        }
      } catch (e) {
        console.warn("[rum] item_added_to_list (meal) event failed:", e);
      }
      return count || 0;
    } catch (err) {
      console.error("addMealToList error:", err.message);
      if (classifyFetchError(err) === 'transient') { reportTransientFailure(); } else { setError(`Could not add meal to list: ${err.message}`); }
      return 0;
    }
  // loadListItems is a stable hook-scope function (uses refs); matches the
  // ref-based pattern used by updateQty et al.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportTransientFailure, reportSuccess]);

  // lockIn is the add path; the placement decision inside addMealToList keeps
  // an open card's slot. Exposed by name so the board's button and the
  // library's Add read as the same act.
  const lockIn = useCallback(async (mealId) => addMealToList(mealId, 1), [addMealToList]);

  // Add all. { added, readyOnHand }: added = meals that put rows on the list; readyOnHand =
  // meals with every ingredient on hand, which 064 stamped ready instead (read from the
  // placement reload addMealToList does after a 0 count).
  const lockInAll = useCallback(async (mealIds) => {
    let added = 0;
    let readyOnHand = 0;
    for (const id of mealIds || []) {
      const count = await addMealToList(id, 1);
      if (count > 0) added += 1;
      else if (placementsRef.current[id]?.readyAt) readyOnHand += 1;
    }
    return { added, readyOnHand };
  }, [addMealToList]);

  const fetchMealProvenance = useCallback(async () => {
    const db = supabaseRef.current;
    const hh = householdRef.current;
    if (!db || !hh) return {};
    // Joined through list_items so we can key by catalog_item_id (the list
    // renders by item, not by list_item row id). !inner drops rows whose
    // list_item is gone; RLS gates via list_item membership.
    const { data, error: err } = await db
      .from("list_item_meals")
      .select("meal_id, add_count, list_items!inner(catalog_item_id, household_id, deleted_at, status), meals!inner(name, deleted_at, created_by)")
      .eq("list_items.household_id", hh.id)
      .is("list_items.deleted_at", null)
      // A soft-deleted meal must never surface a phantom "from [meal]" badge
      // on Shop (same defect family as the 2026-07-30 phantom-meal bug).
      .is("meals.deleted_at", null);
    if (err) { console.error("fetchMealProvenance error:", err.message); return {}; }
    const map = {};
    (data || []).forEach((row) => {
      const ci = row.list_items?.catalog_item_id;
      if (!ci) return;
      if (!map[ci]) map[ci] = [];
      // created_by is the meal's AUTHOR, which is the only per-meal person
      // this schema records — list_item_meals has no user column, so "who
      // tapped Add" is genuinely not stored anywhere. The provenance line
      // attributes the meal, not the add.
      // status rides along for the board: a meal with no pending row left is
      // "Ready to cook" and offers no remove (there is nothing left to zero).
      map[ci].push({ mealId: row.meal_id, name: row.meals?.name, createdBy: row.meals?.created_by ?? null, addCount: row.add_count, status: row.list_items?.status ?? null });
    });
    return map;
  }, []);

  return {
    quantities, checked, prices, categoryAvgPrices, addedByMap, contributorsMap, household, householdMembers, catalogMap, setCatalogMap, listRows, updateFullName,
    hiddenCatalogItems, loading, householdReady, bootstrapped, error, dismissError,
    // A5: per active household — false until that place's rows have been read once.
    membersLoaded: !!household?.id && membersLoadedFor === household.id,
    cooksLoaded: !!household?.id && cooksLoadedFor === household.id,
    catalogLoaded: !!household?.id && catalogLoadedFor === household.id,
    updateQty, updatePrice, toggleChecked, clearAll, updateBudgetGoal,
    hideItem, deleteItem, removeFromList, createInvite, acceptInvite, restoreHiddenByCategory, unhideItem, toggleStaple, renameItem, refreshCatalog,
    createHousehold, renameHousehold, refreshMembers,
    referralCode, joinHouseholdByCode, discardUnclaimedHousehold,
    fetchMeals, createMeal, updateMeal, setMealOccasion, deleteMeal, requestMealSuggestion, requestRecipeImport, removeMealFromList, decrementMealBatch, createCatalogItem, materializePendingIngredients, addMealToList, fetchMealProvenance, onListChangedRef,
    placements, refreshPlacements, reorderBoard, markCooked, cookIdsByMeal, skipMeal, upNext, planMeal, lockIn, lockInAll, planNoShop, madeBefore, cooks,
    uploadHouseholdPhoto, updateHouseholdBanner, removeHouseholdPhoto,
    addMealPhoto, uploadMealPhoto, saveMealPhoto, setPhotoKind, removeMealPhoto, fetchMealPhotos, rateCook, fetchCookRatings,
    giveMeal, copyGiftPhoto, revokeMealGifts, fetchMealGifts, previewMealGift, recordMealGiftReceipt, acceptGiftPhoto,
    activeCycle, activeSession, openCycle, startSession, wrapUpTrip,
    partnerSession, storeSuggestions, checkedByMap, refreshSessions, ensureSession, setSessionStore, recordListEvent,
    fetchTripReality, answerTripReality,
    supabase: supabaseRef.current,
    _supabase: supabaseRef,
    _household: householdRef,
    _clerkId: clerkIdRef,
    _internalUserId: internalUserIdRef,
    // v2 toasts read the live row count AFTER an add ("14 items on the list");
    // loadListItems sets this ref synchronously, where the `listRows` state
    // the caller closed over is a render behind.
    _listRows: listRowsRef,
  };
}

